import { useCallback, useEffect, useRef, useState } from 'react'
import type { ChangeEvent } from 'react'
import {
  api,
  type AuditRow,
  type DeviceSession,
  type PreviewLink,
  type TwoFactorStatus,
  type WebhookDelivery,
} from '../api'
import { Icon } from './Icon'
import { ConnectedRepository } from './ConnectedRepository'
import { ConnectedIntegrations } from './ConnectedIntegrations'
import { ConnectedSlack } from './ConnectedSlack'
import { ConnectedDirectory } from './ConnectedDirectory'
import { WorkspaceIdentity } from './WorkspaceIdentity'
import { Members } from './Members'
import { Confirm } from './Confirm'
import { CreateDialog } from './CreateDialog'
import { TotpQr } from './TotpQr'
import type { Deletion } from './Deleted'
import { timeAgo } from '../time'
import { ThemePresets } from './ThemePresets'
import { downloadRecoveryCodes } from '../recovery-codes'
import {
  CODE_THEMES,
  DEFAULT_CODE_THEME,
  FONT_PAIRINGS,
  DEFAULT_FONTS,
  THEME_PRESETS,
} from '@staleless/schema'

/**
 * Settings has three subjects, and a screen that does not say which is which.
 *
 * A workspace can hold several documentation sites, and this one screen mixed
 * all three levels in one scroll: the theme of THIS site, the plan of the
 * ORGANISATION that pays for it, and the password of the PERSON reading. So
 * "Delete this workspace" sat four cards under "Accent colour", and nothing on
 * the screen said that changing one of them changed something the other two
 * did not.
 *
 * Three scopes, then, each with its own short list of sections. The scope is
 * chosen at the top of the sub-navigation, because it changes what the list
 * below it means.
 */
export type Scope = 'site' | 'org' | 'account'

interface Section {
  /** `<scope>/<id>`, which is what a card is filed under. */
  at: string
  label: string
  /** One sentence under the heading, saying what this section decides. */
  says: string
}

const SECTIONS: Record<Scope, Section[]> = {
  site: [
    {
      at: 'site/general',
      label: 'General',
      /*
       * What is actually on it. It used to promise "what it announces", and
       * the announcement bar is under Appearance with the rest of what a
       * reader sees — so the sentence sent people to a card that was not
       * there. A section's own sentence is the one thing on it nobody
       * verifies, which is exactly why it goes wrong in a reorganisation.
       */
      says: 'Where the pages live, what this site may spend on a model, and its changelog.',
    },
    {
      at: 'site/appearance',
      label: 'Appearance',
      says: 'The colours, the type and the logo readers see.',
    },
    {
      at: 'site/access',
      label: 'Who can read it',
      says: 'Whether the published site is open, and the links you have shared.',
    },
    {
      at: 'site/domain',
      label: 'Domain',
      says: 'The address readers type, and the certificate behind it.',
    },
    {
      at: 'site/discovery',
      label: 'Discovery',
      says: 'The files crawlers, search engines and language models read.',
    },
    {
      at: 'site/versions',
      label: 'Versions',
      says: 'Frozen copies of the documentation, for the releases they belong to.',
    },
    {
      at: 'site/api',
      label: 'API reference',
      says: 'The description this site builds its reference pages from.',
    },
    {
      at: 'site/repository',
      label: 'GitHub',
      says: 'Where these pages live, when your team keeps them itself.',
    },
  ],
  org: [
    {
      at: 'org/general',
      label: 'General',
      says: 'The workspace itself, and the documentation sites in it.',
    },
    {
      at: 'org/people',
      label: 'People',
      says: 'Who is here, what they may do, and who has been invited.',
    },
    {
      at: 'org/plan',
      label: 'Plan & billing',
      says: 'What this workspace is on, what it costs, and where the card is.',
    },
    {
      at: 'org/agents',
      label: 'Developer tools',
      says: 'Agent access and command-line checks for this documentation.',
    },
    {
      at: 'org/chat',
      label: 'Slack & support',
      says: 'Where alerts go, and what this documentation is measured against.',
    },
    {
      at: 'org/directory',
      label: 'Single sign-on',
      says: 'Signing in through the directory your company already uses.',
    },
    {
      at: 'org/webhooks',
      label: 'Webhooks',
      says: 'What this workspace tells other systems, and whether they answered.',
    },
    {
      at: 'org/activity',
      label: 'Activity log',
      says: 'The changes worth a record, with who made them.',
    },
    {
      at: 'org/export',
      label: 'Export',
      says: 'Every page, as files you can take somewhere else.',
    },
  ],
  account: [
    {
      at: 'account/profile',
      label: 'Profile',
      says: 'Your name, your address, and the account itself.',
    },
    {
      at: 'account/notifications',
      label: 'Notifications',
      says: 'What this product emails you, and how often.',
    },
    {
      at: 'account/security',
      label: 'Security & devices',
      says: 'Your password, your second factor, and where you are signed in.',
    },
  ],
}

/**
 * One card, drawn only where it belongs.
 *
 * A component rather than a conditional around every card, because there are
 * twenty-one of them and a conditional per card is twenty-one chances to wrap
 * the wrong closing tag. `at` is where the card is filed; `here` is where the
 * reader is.
 */
function Card({
  at,
  here,
  danger,
  owns,
  said,
  children,
  ...rest
}: {
  at: string
  here: string
  danger?: boolean
  /**
   * The actions that happen IN this card, by the name `act` files them under.
   *
   * What went wrong and what worked used to be two lines at the top of the
   * screen, above twenty-one cards — so pressing Save on the accent colour put
   * "Saved" a screen and a half from the button, and a refusal from the domain
   * card looked like a refusal from whichever card was being read. A result
   * belongs where the act was.
   */
  owns?: string[]
  said?: { name: string; ok: boolean; text: string } | null
  children: React.ReactNode
} & React.HTMLAttributes<HTMLElement>) {
  if (at !== here) return null
  /* An entry ending in `:` or `-` is a PREFIX. Two families of action are
     named per thing acted on — one per webhook, one per theme preset — and a
     card that owns "the webhooks" owns all of them. */
  const mine =
    said &&
    owns?.some((name) => (/[:-]$/.test(name) ? said.name.startsWith(name) : name === said.name))
      ? said
      : null
  return (
    <section className={`settings__card${danger ? ' settings__card--danger' : ''}`} {...rest}>
      {children}
      {mine && (
        <p className={mine.ok ? 'card__done' : 'card__failed'} role="status">
          <Icon name={mine.ok ? 'check' : 'alert'} size={13} />
          {mine.text}
        </p>
      )}
    </section>
  )
}

function ThemeAssetPreview({
  src,
  alt,
  className,
  iconSize = 15,
}: {
  src: string | null | undefined
  alt: string
  className: string
  iconSize?: number
}) {
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    setFailed(false)
  }, [src])

  if (!src || failed) {
    return (
      <span className={`theme__asset-preview ${className}`} aria-hidden="true">
        <Icon name="image" size={iconSize} />
      </span>
    )
  }

  return (
    <img
      src={src}
      alt={alt}
      className={`theme__asset-preview ${className}`}
      onError={() => setFailed(true)}
    />
  )
}

/**
 * A country code as a person's word for it.
 *
 * The same treatment the Analytics screen gives one, and for the same reason:
 * "GB" is a fact about a header and "United Kingdom" is what somebody
 * checking their own sessions is trying to read.
 */
function regionName(code: string): string {
  try {
    return new Intl.DisplayNames(undefined, { type: 'region' }).of(code) ?? code
  } catch {
    return code
  }
}

/** A plain `#rrggbb`, the only shape the accent setter accepts. */
/**
 * Where "Let's talk" goes.
 *
 * A mailto rather than a form, and that is the whole of the Enterprise path
 * today: the tier is sold in a conversation, and a form that collects a company
 * size into a table nobody reads is a worse first impression than a reply from
 * a person. The subject line is there so the reply lands somewhere findable.
 */
const ENTERPRISE_MAILTO =
  'mailto:hello@staleless.com?subject=' + encodeURIComponent('Staleless Enterprise')

const HEX_COLOUR = /^#[0-9a-f]{6}$/i

/**
 * What an audit action means, in words rather than the enum the server
 * stores. Read as a sentence after the actor's name: "Ada Lovelace published".
 *
 * The fallback is not a guess dressed up as one: a future action this map has
 * not learned yet still reads as "someone did page.delete" rather than
 * disappearing or crashing the screen — legible, if not yet polished.
 */
function describeAudit(action: string): string {
  const KNOWN: Record<string, string> = {
    commit: 'committed a change',
    push: 'pushed a draft for review',
    publish: 'published',
    rollback: 'rolled back a publication',
    'branch.create': 'started a draft',
    'branch.merge': 'merged a draft',
    'branch.catch_up': 'caught a draft up with the live site',
    'stage.write': 'staged an edit',
    'stage.move': 'staged a move',
    'stage.delete': 'staged a deletion',
    'stage.unstage': 'unstaged a change',
    'page.move': 'moved a page',
    'page.delete': 'deleted a page',
    'redirects.collapse': 'cleaned up stale redirects',
    'preview.create': 'shared a preview',
    'preview.revoke': 'revoked a preview',
    'invitation.create': 'invited a member',
    'invitation.revoke': 'withdrew an invitation',
    'member.remove': 'removed a member',
    'member.role_change': 'changed a member’s role or permissions',
    'domain.set': 'changed the domain',
    'site_password.set': 'set a site passphrase',
    'site_password.clear': 'removed the site passphrase',
    'project.create': 'created a project',
    import: 'imported documentation',
    'webhook.create': 'added a webhook endpoint',
    'webhook.remove': 'removed a webhook endpoint',
    'webhook.test': 'sent a webhook test event',
    'webhook.enable': 'turned a webhook endpoint back on',
    'webhook.disable': 'turned a webhook endpoint off',
    'api_token.mint': 'created an API token',
    'api_token.revoke': 'revoked an API token',
    'analytics.retention': 'changed how long reader analytics are kept',
    'build.retry': 'ran a site build again',
    'build.rebuild': 'rebuilt a published site',
    'import.retry': 'ran an import again',
    'comment.add': 'left a comment',
    'comment.resolve': 'resolved a conversation',
    'comment.reopen': 'reopened a conversation',
    'conflict.resolve': 'resolved a conflict with the published site',
    'domain.verify': 'verified the domain',
    'page.restore': 'restored a page from the trash',
    'trash.discard': 'discarded a page from the trash',
    'project.delete': 'deleted a project',
    'workspace.delete': 'deleted the workspace',
    'review.request': 'asked for a review',
    'review.comment': 'commented on a review',
    'review.approve': 'approved a draft',
    'review.request_changes': 'asked for changes on a draft',
    'site_access.workspace_only': 'made the site readable only inside this workspace',
    'version.cut': 'cut a documentation version',
    'version.retire': 'retired a documentation version',
    'version.deprecate': 'marked a version as no longer maintained',
    'version.undeprecate': 'took the “no longer maintained” mark off a version',
    'workspace.rename': 'renamed the workspace',
    'workspace.two_factor_required': 'required two-step sign-in for this workspace',
    'workspace.two_factor_optional': 'stopped requiring two-step sign-in for this workspace',
    'announcement.set': 'put a banner on the site',
    'announcement.clear': 'took the banner off the site',
    'api_spec.set': 'changed which file the API reference is built from',
    'api_spec.clear': 'stopped pointing at a specific OpenAPI file',
    'theme.set': 'changed how the site looks',
    'project_role.set': 'changed what somebody may do in one documentation site',
    'project_role.clear': 'went back to the workspace’s answer for somebody',
    'asset.remove': 'deleted a file from the library',
    'workspace.restore': 'brought this workspace back after it was deleted',
    'ask.enabled': 'let this site answer readers’ questions',
    'ask_log.enabled': 'started keeping the questions readers ask',
    'ai_cap.set': 'set how much this site may ask a model for in a day',
    'workspace.transferred': 'handed this workspace to somebody else',
    'ask_log.disabled': 'stopped keeping the questions readers ask',
    'writing_help.enabled': 'let writers here ask for help with the words',
    'writing_help.disabled': 'stopped writers here asking for help with the words',
    'ask.disabled': 'stopped this site answering readers’ questions',
    'plan.upgraded': 'upgraded the plan',
    'plan.downgraded': 'went back to the free plan',
    'payment.failed': 'a payment did not go through',
    'payment.recovered': 'a payment went through again',
  }
  return KNOWN[action] ?? `did ${action.replace(/[._]/g, ' ')}`
}

/**
 * The one line of extra context a row's payload is worth showing — which
 * branch, page, domain or commit, when the action recorded one. Never the
 * whole payload: `site_password.set` carries none of these on purpose, and
 * showing the raw object would be the one place this screen could leak
 * something it took care never to store.
 */
function auditDetail(row: AuditRow): string | null {
  const p = row.payload
  const parts: string[] = []
  for (const key of ['branch', 'path', 'domain', 'email', 'platform', 'role', 'url'] as const) {
    const value = p[key]
    if (typeof value === 'string' && value) parts.push(value)
  }
  if (typeof p['pr'] === 'number') parts.push(`PR #${p['pr']}`)
  if (row.commitSha) parts.push(row.commitSha.slice(0, 7))
  return parts.length > 0 ? parts.join(' · ') : null
}
/**
 * A delivery's state, in one word.
 *
 * `queued` means two different things to a person and the difference is the
 * whole story: a delivery nobody has tried yet is waiting, and one that has
 * been tried and pushed out is retrying. The database calls both of them
 * queued, correctly — it is one state — so this is where the difference
 * belongs.
 *
 * ONE WORD because it goes in a badge, and a badge is a pill of uppercase
 * monospace that does not wrap — "GAVE UP AFTER 5 OF 5" would be a stripe
 * across a phone. How many attempts it took is a fact for the line
 * underneath, where there is room for it and where the rest of the detail
 * already is.
 */
function describeDelivery(row: WebhookDelivery): string {
  if (row.status === 'done') return 'Delivered'
  if (row.status === 'running') return 'Sending'
  if (row.status === 'failed') return 'Gave up'
  return row.attempts === 0 ? 'Waiting' : 'Retrying'
}

/** Which badge a delivery earns. Never a colour on its own: the word carries it. */
function deliveryBadge(row: WebhookDelivery): string {
  if (row.status === 'done') return 'badge badge--accent'
  if (row.status === 'failed') return 'badge badge--danger'
  return 'badge badge--quiet'
}

/** A short, human name for the browser that a session's user agent describes. */
function describeDevice(userAgent: string | null): string {
  if (!userAgent) return 'Unknown device'
  const os = /iPhone/.test(userAgent)
    ? 'iPhone'
    : /iPad/.test(userAgent)
      ? 'iPad'
      : /Android/.test(userAgent)
        ? 'Android'
        : /Mac OS X/.test(userAgent)
          ? 'Mac'
          : /Windows/.test(userAgent)
            ? 'Windows'
            : /Linux/.test(userAgent)
              ? 'Linux'
              : 'Unknown device'
  // Checked in this order because a Chrome user agent also contains "Safari/",
  // and an Edge one also contains "Chrome/" — the more specific token wins only
  // by being tested first.
  const browser = /Edg\//.test(userAgent)
    ? 'Edge'
    : /Chrome\//.test(userAgent)
      ? 'Chrome'
      : /Firefox\//.test(userAgent)
        ? 'Firefox'
        : /Safari\//.test(userAgent)
          ? 'Safari'
          : ''
  return browser ? `${browser} on ${os}` : os
}

/**
 * The active project's settings, in one place a person can find.
 *
 * Everything here existed as an API and nothing else: the passphrase that makes
 * a site private (built, enforced, invisible), the domain a site answers on,
 * and the repository — which the top bar used to show as if it were a name.
 * Spec 46 and 28 both assumed screens that did not exist.
 *
 * Per PROJECT, because a workspace holds several sites: the domain field here
 * names where Ada answers, not where Classic does.
 */
type Settings = Awaited<ReturnType<typeof api.workspaceSettings>>

export function SettingsScreen({
  onChanged,
  onSignedOut,
  onEditVersion,
  onDeleted,
  onOpenPlace,
  at,
  onAt,
}: {
  /**
   * Which section to open on, when the shell has been asked for one.
   *
   * The People row in the sidebar, the invite item in the switcher menu and
   * the palette all mean "the organisation's people", and all three used to
   * open a drawer. They ask for `org/people` now; anything else lands where
   * this screen was left.
   */
  at?: string | null
  /** Where the reader moved to, so the shell can forget its request. */
  onAt?: (at: string) => void
  onChanged: () => void
  /** Called after this browser's OWN session was the one just revoked. */
  onSignedOut: () => void
  /**
   * The places that left the shell's rail and live here now: Import,
   * Variables, Redirects, Analytics and Developers. Entries at the top of this screen, so
   * everything a workspace can do still has a door somebody can find.
   */
  onOpenPlace?: (
    place: 'import' | 'variables' | 'redirects' | 'analytics' | 'words' | 'search' | 'developers',
  ) => void
  /**
   * A draft was started on a version, and the shell should open it.
   *
   * Settings can make the draft but has no business switching screens on its
   * own — the same reason every other action here reports upward rather than
   * navigating.
   */
  onEditVersion?: (branch: string) => void
  /**
   * The workspace, or the account, has just been deleted.
   *
   * Reported upward rather than shown here, and this is the one place on the
   * screen where that is not a matter of taste. A deleted workspace stops
   * resolving for everybody at once, so the very next request this screen
   * makes answers 403 and the shell — correctly — replaces it. A confirmation
   * drawn on the card would be gone before it was read. See `Deleted`.
   */
  onDeleted: (what: Deletion) => void
}) {
  const [settings, setSettings] = useState<Settings | null>(null)
  const [error, setError] = useState<string | null>(null)
  /** What the last act did, and which card it belongs to. See `Card`. */
  const [said, setSaid] = useState<{ name: string; ok: boolean; text: string } | null>(null)
  /**
   * Which of the three subjects is open, and which section of it.
   *
   * One string, `<scope>/<section>`, because the two are never independent: a
   * section belongs to exactly one scope, and holding them apart is holding
   * one fact in two places. Remembered for the session only — coming back to
   * Settings should land where you left it, and coming back tomorrow should
   * land on the front of it.
   */
  const [where, setWhere] = useState(at ?? 'site/general')
  const scope = (where.split('/')[0] ?? 'site') as Scope

  /* A request from the shell wins once, and then the screen is the reader's. */
  useEffect(() => {
    if (at) setWhere(at)
  }, [at])

  const goTo = useCallback(
    (next: string) => {
      setWhere(next)
      onAt?.(next)
    },
    [onAt],
  )

  const [domain, setDomain] = useState('')
  const [passphrase, setPassphrase] = useState('')
  const [accent, setAccent] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [customCss, setCustomCss] = useState('')
  /**
   * Whether this site answers readers' questions.
   *
   * Local state rather than reading `settings.askEnabled` straight through,
   * for the reason every other switch on this screen holds its own: the write
   * is a round trip, so a controlled box bound to the loaded settings snaps
   * back the instant it is clicked and only moves when the reload lands. That
   * is what the gate caught — "clicking the checkbox did not change its
   * state" — and a person would have seen the same flicker.
   */
  const [askEnabled, setAskEnabled] = useState(false)
  /** The same, for §22's help with the words. Its own switch; see the card. */
  const [writingHelp, setWritingHelp] = useState(false)
  /** And whether the questions readers ask are kept at all — §21. */
  const [askLog, setAskLog] = useState(false)
  /**
   * What the AI features have cost today (§55).
   *
   * Loaded beside the settings rather than folded into them: it changes as
   * people work, and it is the one thing on this card that is a fact about
   * yesterday's decisions rather than a decision to take now.
   */
  const [usage, setUsage] = useState<Awaited<ReturnType<typeof api.aiUsage>> | null>(null)
  /**
   * The owner's own ceiling, as typed. Empty means "what the plan includes",
   * which is also what the server stores as null.
   */
  const [cap, setCap] = useState('')
  const [emailNotifications, setEmailNotifications] = useState(true)
  const [digest, setDigest] = useState<'weekly' | 'off'>('weekly')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month')
  const logoInput = useRef<HTMLInputElement>(null)
  const iconInput = useRef<HTMLInputElement>(null)
  const socialInput = useRef<HTMLInputElement>(null)
  /** The line above every page, as somebody is typing it. */
  const [banner, setBanner] = useState('')
  const [bannerHref, setBannerHref] = useState('')
  const [ctaEnabled, setCtaEnabled] = useState(false)
  const [ctaLabel, setCtaLabel] = useState('Get started')
  const [ctaHref, setCtaHref] = useState('')

  const [sessions, setSessions] = useState<DeviceSession[] | null>(null)
  const [sessionsError, setSessionsError] = useState<string | null>(null)
  /** Live share links, and what went wrong reading or withdrawing them. */
  const [previews, setPreviews] = useState<PreviewLink[] | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)

  /**
   * Two-step sign-in.
   *
   * Four states, and the screen shows exactly one: off, mid-enrolment, the
   * recovery codes (once), and on. `enrolling` holds a secret the server has
   * stored but not confirmed — abandoning it here leaves the account exactly
   * as it was, which is the whole reason enrolment has two halves.
   */
  const [twoFactor, setTwoFactor] = useState<TwoFactorStatus | null>(null)
  const [enrolling, setEnrolling] = useState<{
    secret: string
    readable: string
    uri: string
  } | null>(null)
  const [totpCode, setTotpCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)
  const [totpBusy, setTotpBusy] = useState(false)
  const [totpError, setTotpError] = useState<string | null>(null)

  const loadTwoFactor = useCallback(async () => {
    try {
      setTwoFactor(await api.twoFactorStatus())
    } catch {
      // Not every deployment has it wired up. A card that cannot say anything
      // true says nothing at all.
      setTwoFactor(null)
    }
  }, [])
  useEffect(() => void loadTwoFactor(), [loadTwoFactor])

  const beginTwoFactor = useCallback(async () => {
    setTotpBusy(true)
    setTotpError(null)
    try {
      setEnrolling(await api.startTwoFactor())
      setTotpCode('')
    } catch (e) {
      setTotpError(e instanceof Error ? e.message : String(e))
    } finally {
      setTotpBusy(false)
    }
  }, [])

  const finishTwoFactor = useCallback(async () => {
    setTotpBusy(true)
    setTotpError(null)
    try {
      const { recoveryCodes: codes } = await api.confirmTwoFactor(totpCode.trim())
      setRecoveryCodes(codes)
      setEnrolling(null)
      setTotpCode('')
      await loadTwoFactor()
    } catch (e) {
      setTotpError(e instanceof Error ? e.message : String(e))
    } finally {
      setTotpBusy(false)
    }
  }, [totpCode, loadTwoFactor])

  const stopTwoFactor = useCallback(async () => {
    setTotpBusy(true)
    setTotpError(null)
    try {
      await api.disableTwoFactor(totpCode.trim())
      setTotpCode('')
      setSaid({
        name: 'totp',
        ok: true,
        text: 'Two-step sign-in is off. Your password is all that stands in front of this account.',
      })
      await loadTwoFactor()
    } catch (e) {
      setTotpError(e instanceof Error ? e.message : String(e))
    } finally {
      setTotpBusy(false)
    }
  }, [totpCode, loadTwoFactor])

  const [auditRows, setAuditRows] = useState<AuditRow[] | null>(null)
  const [auditNext, setAuditNext] = useState<string | null>(null)
  const [auditError, setAuditError] = useState<string | null>(null)
  const [auditBusy, setAuditBusy] = useState(false)

  /**
   * Save where the OpenAPI file is, and reload.
   *
   * The refusal is the service's own sentence — it says what to write instead
   * of what was written — so it is shown rather than reworded.
   */
  const saveSpecPath = async () => {
    setSpecBusy(true)
    setSpecError(null)
    try {
      await api.setApiSpecPath(specPath.trim() === '' ? null : specPath.trim())
      await load()
    } catch (e) {
      setSpecError(e instanceof Error ? e.message : String(e))
    } finally {
      setSpecBusy(false)
    }
  }

  /**
   * What the AI features have spent today, and the ceiling they are spending
   * against — read again after the cap changes, so the meters and the number
   * beside them cannot disagree.
   */
  const loadUsage = useCallback(async () => {
    try {
      const said = await api.aiUsage()
      setUsage(said)
      setCap(said.cap === null ? '' : String(said.cap))
    } catch {
      setUsage(null)
    }
  }, [])

  const load = useCallback(async () => {
    try {
      const next = await api.workspaceSettings()
      setSettings(next)
      setDomain(next.domain ?? '')
      setAccent(next.theme.accent ?? '')
      // What they typed, not the scoped form the build reads — see migration 0066.
      setCustomCss(next.theme.customCss ?? '')
      setSpecPath(next.apiSpecPath ?? '')
      setAskEnabled(next.askEnabled)
      setWritingHelp(next.writingHelpEnabled)
      setAskLog(next.askLogEnabled)
      void loadUsage()
      void api
        .apiSpec()
        .then(setSpecFound)
        .catch(() => setSpecFound(null))
      setBanner(next.theme.announcement?.text ?? '')
      setBannerHref(next.theme.announcement?.href ?? '')
      setCtaEnabled(Boolean(next.theme.cta))
      setCtaLabel(next.theme.cta?.label ?? 'Get started')
      setCtaHref(next.theme.cta?.href ?? '')
      setError(null)
    } catch (e) {
      // The screen itself could not be read, which is not a card's business:
      // the loading branch above renders this one.
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [])
  useEffect(() => void load(), [load])

  const [deleteName, setDeleteName] = useState('')
  const [deleting, setDeleting] = useState(false)
  const destroy = useCallback(async () => {
    setDeleting(true)
    setSaid(null)
    try {
      const { graceDays } = await api.deleteWorkspace(deleteName)
      // The workspace no longer resolves for anybody; whatever this browser
      // remembers about it is a dead pointer, and the screen below leaves for
      // `/` when the person is ready.
      localStorage.removeItem('staleless:workspace')
      onDeleted({ kind: 'workspace', name: deleteName.trim(), graceDays })
    } catch (e) {
      setSaid({
        name: 'workspace-delete',
        ok: false,
        text: e instanceof Error ? e.message : String(e),
      })
      setDeleting(false)
    }
  }, [deleteName, onDeleted])

  const [accountEmail, setAccountEmail] = useState('')
  /**
   * Moving the address this account signs in with.
   *
   * It was set at sign-up and permanent, so somebody who changed job or left a
   * shared address behind was stuck signing in as somebody they no longer are.
   * Two fields, because the current password is what proves the person at the
   * keyboard is the owner — the same reason changing a password asks for it.
   */
  const [newEmail, setNewEmail] = useState('')
  const [emailPassword, setEmailPassword] = useState('')
  const [emailSentTo, setEmailSentTo] = useState<string | null>(null)
  /** The address on the account now, so the screen can name what is changing. */
  const [signInEmail, setSignInEmail] = useState<string | null>(null)
  const [accountBusy, setAccountBusy] = useState(false)
  const destroyAccount = useCallback(async () => {
    setAccountBusy(true)
    setSaid(null)
    try {
      await api.deleteAccount(accountEmail)
      onDeleted({ kind: 'account', email: accountEmail.trim() })
    } catch (e) {
      setSaid({
        name: 'account-delete',
        ok: false,
        text: e instanceof Error ? e.message : String(e),
      })
      setAccountBusy(false)
    }
  }, [accountEmail, onDeleted])

  const [changelog, setChangelog] = useState<Awaited<ReturnType<typeof api.writeChangelog>> | null>(
    null,
  )
  const [versions, setVersions] = useState<
    { name: string; label: string; deprecated: boolean }[] | null
  >(null)
  const [versionBusy, setVersionBusy] = useState(false)
  /** The version whose "stop publishing this" question is open. */
  const [retiring, setRetiring] = useState<string | null>(null)
  const loadVersions = useCallback(async () => {
    try {
      setVersions((await api.versions()).versions)
    } catch (e) {
      /**
       * NOT an empty list.
       *
       * The card renders "No versions yet — everything is latest" for an empty
       * one, which is a statement about the customer's data — and a request
       * that failed says nothing about their data. Their versions are still
       * cut, still frozen and still answering at `/v/<name>/`; what has gone is
       * our ability to ask. Saying they are gone is the wrong reason for a
       * blank, and it takes "Fix this version" off the screen with it, so the
       * one door out of a frozen typo disappears on a dropped request.
       *
       * Left as `null`, which renders nothing at all, with the reason in the
       * banner every other failure on this screen uses. A reload asks again.
       */
      setVersions(null)
      setSaid({ name: 'versions', ok: false, text: e instanceof Error ? e.message : String(e) })
    }
  }, [])
  useEffect(() => void loadVersions(), [loadVersions])

  /**
   * Freezing what is published as a version.
   *
   * Two `window.prompt`s asked this: the name for the URL, then a second box
   * for what readers should call it. Two boxes in sequence for one act — and
   * cancelling the second one silently accepted the slug as the label, so a
   * version readers see named "v1" rather than "Version 1" was the product's
   * doing rather than anybody's decision. The whole explanation of what a
   * version IS rode in the first prompt's message, joined with a newline,
   * where it could not be styled and disappeared the moment it was answered.
   *
   * It is one dialog now, with both questions on it and the rule under each.
   * The refusal is thrown rather than swallowed so the dialog keeps it.
   */
  const [cutting, setCutting] = useState(false)
  const cutVersion = useCallback(
    async (name: string, label: string) => {
      setVersionBusy(true)
      setSaid(null)
      try {
        await api.cutVersion(name, label.trim() || name)
        await loadVersions()
        setCutting(false)
        setSaid({
          name: 'versions',
          ok: true,
          text: `Froze the live site as "${name}". It appears at /v/${name}/ the next time the site builds.`,
        })
      } finally {
        setVersionBusy(false)
      }
    },
    [loadVersions],
  )

  const [domainStatus, setDomainStatus] = useState<Awaited<
    ReturnType<typeof api.domainStatus>
  > | null>(null)
  const loadDomain = useCallback(async () => {
    try {
      setDomainStatus(await api.domainStatus())
    } catch {
      setDomainStatus(null)
    }
  }, [])
  useEffect(() => void loadDomain(), [loadDomain])

  const download = useCallback(async () => {
    setBusy('export')
    setSaid(null)
    try {
      const { blob, filename } = await api.exportArchive()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
      setSaid({
        name: 'export',
        ok: true,
        text: 'Export downloaded. Everything is in it — pages, navigation, images, every version.',
      })
    } catch (e) {
      setSaid({ name: 'export', ok: false, text: e instanceof Error ? e.message : String(e) })
    } finally {
      setBusy(null)
    }
  }, [])

  const loadSessions = useCallback(async () => {
    try {
      const { sessions: list } = await api.sessions()
      setSessions(list)
      setSessionsError(null)
    } catch (e) {
      setSessionsError(e instanceof Error ? e.message : String(e))
    }
  }, [])
  useEffect(() => void loadSessions(), [loadSessions])

  /**
   * The projects in this workspace, so one made by mistake can be removed.
   * Making one was possible from the first day; unmaking one was not.
   */
  const [projects, setProjects] = useState<{ id: string; slug: string; name: string }[]>([])
  const [activeProject, setActiveProject] = useState<string | null>(null)
  const [removing, setRemoving] = useState<string | null>(null)
  const loadProjects = useCallback(async () => {
    try {
      const found = await api.projects()
      setProjects(found.projects)
      setActiveProject(found.active)
    } catch {
      // A workspace whose projects cannot be listed still has settings worth
      // showing; the card simply does not appear.
    }
  }, [])
  useEffect(() => void loadProjects(), [loadProjects])

  /**
   * The name to show, from the session rather than from the workspace: it
   * belongs to the person, not to the project this screen is about.
   */
  useEffect(() => {
    void api
      .me()
      .then((session) => {
        setDisplayName((was) => was || session.user.displayName)
        setSignInEmail(session.user.email)
        setEmailNotifications(session.user.emailNotifications)
        setDigest(session.user.digest)
      })
      .catch(() => undefined)
  }, [])

  const loadPreviews = useCallback(async () => {
    try {
      setPreviews((await api.previews()).previews)
      setPreviewError(null)
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : String(e))
    }
  }, [])
  useEffect(() => void loadPreviews(), [loadPreviews])

  /**
   * The audit trail is the WORKSPACE's — every project's sensitive changes,
   * including the ones this person has no standing in — so it reads the
   * workspace role rather than the standing in the project on screen.
   */
  const auditRole = settings?.you?.workspaceRole ?? settings?.you?.role
  const mayReadAudit = auditRole === 'owner' || auditRole === 'admin'

  const loadAudit = useCallback(async () => {
    try {
      const { rows, nextBefore } = await api.auditLog()
      setAuditRows(rows)
      setAuditNext(nextBefore)
      setAuditError(null)
    } catch (e) {
      setAuditError(e instanceof Error ? e.message : String(e))
    }
  }, [])
  useEffect(() => {
    // Owner or admin only, and asked for only then: the server would refuse
    // anybody else, and this way a viewer's screen never has to render — or
    // recover from — a refusal it was never going to be shown in the first
    // place. `settings` loads first, so this fires once the role is known.
    //
    // And EVERY time the section is opened, not once when the screen mounts.
    // A log read on mount is a log that goes stale the moment anybody does
    // anything: mint a token, then open the Activity log without leaving
    // Settings, and the act you just took is not in it.
    if (mayReadAudit && where === 'org/activity') void loadAudit()
  }, [mayReadAudit, where, loadAudit])

  const loadMoreAudit = useCallback(async () => {
    if (!auditNext) return
    setAuditBusy(true)
    try {
      const { rows, nextBefore } = await api.auditLog(auditNext)
      setAuditRows((prev) => [...(prev ?? []), ...rows])
      setAuditNext(nextBefore)
    } catch (e) {
      setAuditError(e instanceof Error ? e.message : String(e))
    } finally {
      setAuditBusy(false)
    }
  }, [auditNext])

  /**
   * Webhooks, and the log of what was sent.
   *
   * Read with the same test the activity log uses — owner or admin — because
   * a webhook URL routinely carries a token in its path, which makes the list
   * of endpoints a list of credentials. Named separately from
   * `mayReadAudit` so that a later change to one does not silently move the
   * other.
   */
  const mayReadWebhooks = mayReadAudit
  /** Where this project's OpenAPI file is, as a field somebody is editing. */
  const [specPath, setSpecPath] = useState('')
  /** What the engine actually resolved, which is not always what was typed. */
  const [specFound, setSpecFound] = useState<{
    configured: string | null
    resolved: string | null
    findings: { at: string; says: string; severity: 'error' | 'note' }[]
    usualPlaces: string[]
  } | null>(null)
  const [specError, setSpecError] = useState<string | null>(null)
  const [specBusy, setSpecBusy] = useState(false)
  /** What the last run of the reference generator wrote, for the report. */
  const [reference, setReference] = useState<Awaited<
    ReturnType<typeof api.generateApiReference>
  > | null>(null)
  const [hooks, setHooks] = useState<Awaited<ReturnType<typeof api.webhooks>> | null>(null)
  const [hookError, setHookError] = useState<string | null>(null)
  const [hookUrl, setHookUrl] = useState('')
  const [hookBusy, setHookBusy] = useState<string | null>(null)
  /** The endpoint whose "stop sending to this" question is open. */
  const [removingHook, setRemovingHook] = useState<string | null>(null)
  /**
   * The signing secret, held in memory until the person says they have it.
   *
   * There is no route that reads one back — one that could would turn a stolen
   * session into the ability to forge events into somebody's CI — so this is
   * the only moment it exists outside the database, exactly like the recovery
   * codes above.
   */
  const [hookSecret, setHookSecret] = useState<string | null>(null)

  const loadHooks = useCallback(async () => {
    try {
      setHooks(await api.webhooks())
      setHookError(null)
    } catch (e) {
      setHookError(e instanceof Error ? e.message : String(e))
    }
  }, [])
  useEffect(() => {
    if (mayReadWebhooks) void loadHooks()
  }, [mayReadWebhooks, loadHooks])

  /**
   * Poll only while something is still in flight.
   *
   * A delivery is queued by one process and sent by another, so the screen
   * cannot know the outcome from the reply to the button — and a settings
   * screen that polls for ever costs every open tab a request every few
   * seconds for news that arrives twice a day. Waiting rows are the honest
   * trigger: when there are none, this stops.
   */
  const hooksInFlight =
    hooks?.deliveries.some((d) => d.status === 'queued' || d.status === 'running') ?? false
  useEffect(() => {
    if (!mayReadWebhooks || !hooksInFlight) return
    const timer = window.setInterval(() => void loadHooks(), 2000)
    return () => window.clearInterval(timer)
  }, [mayReadWebhooks, hooksInFlight, loadHooks])

  /** One webhook action, with its own busy key and its own refresh. */
  const hookAct = useCallback(
    async (name: string, work: () => Promise<unknown>, done: string) => {
      setHookBusy(name)
      setHookError(null)
      setSaid(null)
      try {
        await work()
        await loadHooks()
        setSaid({ name: `hook:${name}`, ok: true, text: done })
      } catch (e) {
        setHookError(e instanceof Error ? e.message : String(e))
      } finally {
        setHookBusy(null)
      }
    },
    [loadHooks],
  )

  /**
   * One outcome rather than a map: only one action runs at a time on this
   * screen (`busy` has always held one name), and a card showing the result of
   * something done five minutes ago in another card is worse than none.
   */
  const act = useCallback(
    async (name: string, work: () => Promise<unknown>, done: string) => {
      setBusy(name)
      setSaid(null)
      try {
        await work()
        await load()
        setSaid({ name, ok: true, text: done })
        onChanged()
      } catch (e) {
        setSaid({ name, ok: false, text: e instanceof Error ? e.message : String(e) })
      } finally {
        setBusy(null)
      }
    },
    [load, onChanged],
  )

  /**
   * Upload a logo and make it this project's, in one action.
   *
   * Reuses the EXISTING asset library rather than a theme-specific upload
   * route: `POST /api/assets` already validates and stores the file, and
   * `PATCH /api/theme` only ever needs the key it returns.
   */
  const chooseLogo = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      e.target.value = ''
      if (!file) return
      void act(
        'logo',
        async () => {
          const uploaded = await api.uploadAsset(file)
          await api.updateTheme({ logoKey: uploaded.key })
        },
        'The logo will appear on the site’s next publish.',
      )
    },
    [act],
  )

  /**
   * Upload the browser-tab icon, the same way as the logo.
   *
   * Its own control rather than a second use of the logo: the two are
   * different pictures. A masthead logo is wide and reads at 200 pixels; a
   * tab icon is square and has to survive at sixteen, which is why every
   * product that has both has two files.
   */
  const chooseIcon = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      e.target.value = ''
      if (!file) return
      void act(
        'icon',
        async () => {
          const uploaded = await api.uploadAsset(file)
          await api.updateTheme({ faviconKey: uploaded.key })
        },
        'The tab icon will appear on the site’s next publish.',
      )
    },
    [act],
  )

  const chooseSocialImage = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      e.target.value = ''
      if (!file) return
      void act(
        'social',
        async () => {
          const uploaded = await api.uploadAsset(file)
          await api.updateTheme({ socialImageKey: uploaded.key })
        },
        'The preview image will appear on the site’s next publish.',
      )
    },
    [act],
  )

  /**
   * The device row whose "this is the browser you are in" question is open.
   *
   * It was a `window.confirm` — a box drawn over the list, saying "Continue?"
   * about a row it covered, in a product where every other question of this
   * shape is asked in the row it is about.
   */
  const [signOutHere, setSignOutHere] = useState<string | null>(null)
  /**
   * Whether "Turn it off" on two-step sign-in is waiting for an answer.
   *
   * It happened on one press: the one control on this screen that takes a
   * lock off an account, next to a password field, with no second question.
   */
  const [stoppingTwoFactor, setStoppingTwoFactor] = useState(false)
  /**
   * Revoke one signed-in device.
   *
   * Not routed through `act`: revoking the CURRENT session must not reload
   * these settings afterwards — the next request would 401 — and it has to
   * tell the shell to show the sign-in gate instead of silently leaving a
   * dead session on screen.
   */
  const revoke = useCallback(
    async (s: DeviceSession) => {
      setSignOutHere(null)
      setBusy(`session:${s.id}`)
      setSaid(null)
      try {
        await api.revokeSession(s.id)
        if (s.current) {
          onSignedOut()
          return
        }
        setSaid({ name: 'sessions', ok: true, text: 'That device was signed out.' })
        await loadSessions()
      } catch (e) {
        setSaid({ name: 'sessions', ok: false, text: e instanceof Error ? e.message : String(e) })
      } finally {
        setBusy(null)
      }
    },
    [loadSessions, onSignedOut],
  )

  /**
   * The doors out of Settings, drawn in both states of this screen — a gate
   * or a person heading for Import must not have to wait for the settings
   * read to finish before the door exists.
   */
  const moreLinks = onOpenPlace && (
    <div className="settings__links">
      <button
        className="btn btn--ghost btn--sm"
        data-nav="import"
        onClick={() => onOpenPlace('import')}
      >
        <Icon name="plus" size={13} />
        Import
      </button>
      <button
        className="btn btn--ghost btn--sm"
        data-nav="variables"
        onClick={() => onOpenPlace('variables')}
      >
        <Icon name="braces" size={13} />
        Variables
      </button>
      <button
        className="btn btn--ghost btn--sm"
        data-nav="redirects"
        onClick={() => onOpenPlace('redirects')}
      >
        <Icon name="link" size={13} />
        Redirects
      </button>
      <button
        className="btn btn--ghost btn--sm"
        data-nav="words"
        onClick={() => onOpenPlace('words')}
      >
        <Icon name="text" size={13} />
        Words &amp; style
      </button>
      <button
        className="btn btn--ghost btn--sm"
        data-nav="search"
        onClick={() => onOpenPlace('search')}
      >
        <Icon name="search" size={13} />
        Search
      </button>
      <button
        className="btn btn--ghost btn--sm"
        data-nav="analytics"
        onClick={() => onOpenPlace('analytics')}
      >
        <Icon name="chart" size={13} />
        Analytics
      </button>
    </div>
  )

  if (!settings) {
    return (
      <div className="screen">
        <header className="screen__head">
          <h1>Settings</h1>
        </header>
        {moreLinks}
        {error ? (
          <p className="screen__error">
            <Icon name="alert" size={13} /> {error}
          </p>
        ) : (
          <div className="screen__loading" aria-busy="true">
            <p className="sr-only">Loading…</p>
            <span className="skeleton" />
            <span className="skeleton" />
            <span className="skeleton" />
          </div>
        )}
      </div>
    )
  }

  const mayChange = settings.you?.role === 'owner' || settings.you?.role === 'admin'
  /**
   * The WORKSPACE role, for the decisions that are the company's rather than
   * one site's. `mayChange` reads the project role, which is right for a theme
   * and wrong for anything that leaves the deployment.
   */
  const workspaceRole = settings.you?.workspaceRole ?? settings.you?.role
  const mayDecideAsk = workspaceRole === 'owner' || workspaceRole === 'admin'
  const mayManageBilling = workspaceRole === 'owner' || Boolean(settings.you?.canManageBilling)
  const publishedBase = settings.siteUrl?.replace(/\/+$/, '') ?? null
  const discoveryLinks = publishedBase
    ? [
        {
          label: 'Sitemap',
          path: '/sitemap-index.xml',
          says: 'The URLs search engines should crawl for this published site.',
        },
        {
          label: 'Robots',
          path: '/robots.txt',
          says: 'Crawler instructions, including the sitemap and hidden search index.',
        },
        {
          label: 'LLM index',
          path: '/llms.txt',
          says: 'A compact map of the documentation for language-model readers.',
        },
        {
          label: 'LLM corpus',
          path: '/llms-full.txt',
          says: 'The published documentation as one Markdown document.',
        },
        {
          label: 'RSS feed',
          path: '/rss.xml',
          says: 'A feed of published pages and changes.',
        },
        {
          label: 'Markdown home',
          path: '/index.md',
          says: 'The home page as Markdown, matching the reader page.',
        },
      ].map((link) => ({
        ...link,
        url: `${publishedBase}${link.path}`,
      }))
    : []

  const here = SECTIONS[scope].find((section) => section.at === where) ?? SECTIONS[scope][0]!
  const SCOPES: { id: Scope; label: string; says: string }[] = [
    {
      id: 'site',
      label: settings.project?.name ?? 'This docs site',
      says: 'This documentation site',
    },
    { id: 'org', label: settings.name, says: 'The organisation that owns it' },
    /*
     * The address, not just the words "your account". Somebody signed in to
     * two workspaces on one machine — or on a shared one — is about to change
     * a password and a set of devices, and "Your account" is true of whichever
     * account they are in. The board says it, and the screen already has it.
     */
    { id: 'account', label: signInEmail || 'Your account', says: 'You, everywhere' },
  ]

  return (
    <div className="screen screen--settings">
      <header className="screen__head">
        <h1>Settings</h1>
        <p className="screen__sub">{here.says}</p>
      </header>

      <div className="settings__frame">
        <nav className="settings__side" aria-label="Settings">
          {/*
            The scope, above the sections rather than beside them.

            It changes what the list under it MEANS — the same word "General"
            is three different screens depending on which of the three
            subjects is chosen — so it is a decision taken first, and it is
            drawn as one.
          */}
          <div
            className="settings__scopes"
            role="tablist"
            aria-label="What these settings are about"
          >
            {SCOPES.map((s) => (
              <button
                key={s.id}
                role="tab"
                aria-selected={scope === s.id}
                data-scope={s.id}
                className={`settings__scope${scope === s.id ? ' is-on' : ''}`}
                onClick={() => goTo(SECTIONS[s.id][0]!.at)}
              >
                <span className="settings__scope-what">{s.says}</span>
                <span className="settings__scope-name truncate">{s.label}</span>
              </button>
            ))}
          </div>

          <ul className="settings__nav">
            {SECTIONS[scope].map((section) => (
              <li key={section.at}>
                <button
                  className={`settings__navrow${where === section.at ? ' is-on' : ''}`}
                  data-section={section.at}
                  aria-current={where === section.at ? 'page' : undefined}
                  onClick={() => goTo(section.at)}
                >
                  {section.label}
                </button>
              </li>
            ))}
          </ul>

          {/*
            The places that are screens of their own. They belong to this site
            the way the sections above do, and they are drawn here rather than
            in the shell's rail because a writer meets them once a month.
          */}
          {scope === 'site' && moreLinks}
        </nav>

        <div className="settings__body">
          {!mayChange && (
            <p className="screen__warn">
              <Icon name="info" size={13} /> Only an owner or admin can change these. You can read
              them.
            </p>
          )}

          {/*
            People, as a screen. It used to be a drawer over whatever somebody
            was writing — a roster, an invite form and a legend, in a 420px
            column with a scroll of its own — and who is in a workspace is a
            fact ABOUT the workspace, beside the plan that decides how many of
            them there may be.
          */}
          {where === 'org/people' && <Members onSeePlans={() => goTo('org/plan')} />}

          <Card at="org/agents" here={where} data-developer-tools>
            <h2>Developer tools</h2>
            <p className="home__quiet">
              MCP, CLI setup and scoped access tokens now have a dedicated place in the main
              navigation, so they stay visible outside workspace administration.
            </p>
            {onOpenPlace && (
              <button className="btn btn--tinted" onClick={() => onOpenPlace('developers')}>
                <Icon name="code" size={14} /> Open Developers
              </button>
            )}
          </Card>

          <Card
            at="site/access"
            here={where}
            said={said}
            owns={['passphrase', 'public', 'workspace-only']}
          >
            <h2>Who can read the published site</h2>
            <p className="home__quiet">
              {settings.visibility === 'password'
                ? 'Readers need a passphrase. It is never shown again — replace it if it is lost.'
                : settings.visibility === 'workspace'
                  ? 'Only people in this workspace can read it. They sign in with their own account; there is no shared secret to pass around or take back.'
                  : 'Anybody with the address can read it.'}
            </p>
            <label className="field">
              <span className="label">Reader passphrase</span>
              <div className="settings__row">
                <input
                  type="password"
                  autoComplete="new-password"
                  placeholder={
                    settings.sitePasswordSet
                      ? 'New passphrase (replaces the old one)'
                      : 'A passphrase, 12+ characters'
                  }
                  value={passphrase}
                  disabled={!mayChange}
                  onChange={(e) => setPassphrase(e.target.value)}
                />
                {/* Tinted, not filled. Two ways to restrict a site sit in this one card,
                and both were filled accents — so the card asked a question with
                two answers and shouted both of them. Settings is a screen of
                twenty-two independent cards and has no single action, which is
                what BRAND's one-primary-per-screen rule is about; every card's
                own action is important without being THE action. */}
                <button
                  className="btn btn--tinted"
                  disabled={!mayChange || passphrase.trim().length === 0 || busy !== null}
                  onClick={() =>
                    void act(
                      'passphrase',
                      async () => {
                        await api.setSitePassword(passphrase.trim())
                        setPassphrase('')
                      },
                      'The site now asks readers for the passphrase.',
                    )
                  }
                >
                  {busy === 'passphrase' ? <Icon name="spinner" size={13} /> : null}
                  {settings.sitePasswordSet ? 'Replace passphrase' : 'Require a passphrase'}
                </button>
                {settings.sitePasswordSet && (
                  <button
                    className="btn btn--ghost"
                    disabled={!mayChange || busy !== null}
                    onClick={() =>
                      void act(
                        'public',
                        () => api.setSitePassword(null),
                        'The site is public again. Every reader session was signed out.',
                      )
                    }
                  >
                    Make it public
                  </button>
                )}
              </div>
            </label>

            <div className="settings__row settings__row--stack">
              <p className="home__quiet">
                Or leave the passphrase out of it: readers sign in with their own Staleless account
                and the site checks whether they are in this workspace. Removing somebody from the
                workspace takes their access with it, which a shared passphrase cannot do — the site
                rebuilds to carry the change, so it takes a moment rather than being instant.
              </p>
              <div className="settings__row">
                <button
                  className={`btn ${settings.visibility === 'workspace' ? 'btn--ghost' : 'btn--tinted'}`}
                  disabled={!mayChange || busy !== null}
                  onClick={() =>
                    void act(
                      'workspace-only',
                      () => api.setWorkspaceOnly(),
                      settings.visibility === 'workspace'
                        ? 'Readers will be signed out as soon as the site rebuilds. Everyone in the workspace can sign in again.'
                        : 'Only people in this workspace can read the site now.',
                    )
                  }
                >
                  {busy === 'workspace-only' ? <Icon name="spinner" size={13} /> : null}
                  {settings.visibility === 'workspace'
                    ? 'Sign out every reader'
                    : 'Only people in this workspace'}
                </button>
                {settings.visibility === 'workspace' && (
                  <button
                    className="btn btn--ghost"
                    disabled={!mayChange || busy !== null}
                    onClick={() =>
                      void act(
                        'public',
                        () => api.setSitePassword(null),
                        'The site is public again. Every reader session was signed out.',
                      )
                    }
                  >
                    Make it public
                  </button>
                )}
              </div>
            </div>
          </Card>

          {/*
        Share links, and the only thing anybody can still do about one.

        Sharing a draft mints a link that shows unpublished work to somebody
        with no account, and the link is shown ONCE — only its hash is kept, so
        it can never be displayed again. Revoking was built with it and had no
        screen: the API could withdraw a link and nothing in the product could
        ask it to. That is a credential a customer hands out and cannot take
        back, which is the failure this card exists to prevent.
      */}
          <Card at="site/access" here={where} data-previews>
            <h2>Shared preview links</h2>
            <p className="home__quiet">
              Each one shows a draft — including work nobody has committed — to anybody holding the
              link, without an account. The link itself is not stored, so it cannot be shown again;
              taking one back is what can be done here. They stop working on their own after the
              date shown.
            </p>
            {previewError && (
              <p className="screen__error">
                <Icon name="alert" size={13} /> {previewError}
              </p>
            )}
            {previews && previews.length === 0 && (
              <p className="nothing" data-previews-empty>
                No live preview links. Sharing a draft from its review panel makes one.
              </p>
            )}
            {previews && previews.length > 0 && (
              <ul className="people">
                {previews.map((link) => {
                  const expired = new Date(link.expiresAt) <= new Date()
                  return (
                    <li key={link.id} className="people__row" data-preview-row>
                      <span className="people__who">
                        <span className="people__name">
                          <code>{link.branch}</code>
                          {expired && <span className="badge">expired</span>}
                        </span>
                        <span className="people__email">
                          Shared {new Date(link.createdAt).toLocaleDateString()}
                          {' · '}
                          {expired
                            ? `stopped working ${new Date(link.expiresAt).toLocaleDateString()}`
                            : `works until ${new Date(link.expiresAt).toLocaleDateString()}`}
                        </span>
                      </span>
                      {/*
                    Expired links keep their row and lose the button: the link
                    is already dead — the database refuses a revoked and an
                    expired token in the same breath — and offering to revoke
                    one suggests it was not.
                  */}
                      {!expired && settings.you?.canPublish && (
                        <button
                          className="btn btn--danger btn--sm"
                          disabled={busy === `preview:${link.id}`}
                          onClick={() => {
                            setBusy(`preview:${link.id}`)
                            void api
                              .revokePreview(link.id)
                              .then(loadPreviews)
                              .catch((e: unknown) =>
                                setPreviewError(e instanceof Error ? e.message : String(e)),
                              )
                              .finally(() => setBusy(null))
                          }}
                        >
                          {busy === `preview:${link.id}` ? <Icon name="spinner" size={13} /> : null}
                          Revoke
                        </button>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </Card>

          {/* A stable hook for the gate, like the Theme and webhooks cards: the
          words inside change with the state, so a test that finds this card by
          one of its sentences stops finding it exactly when that sentence is
          the thing being checked. */}
          <Card
            at="site/domain"
            here={where}
            said={said}
            owns={['domain', 'verify']}
            data-domain-card
          >
            <h2>Domain</h2>
            <p className="home__quiet">
              Where this site answers. Just the hostname — no scheme, port or path. Each site in the
              workspace has its own, and a domain belongs to one site: it has to be proved before
              this site will answer for it.
            </p>
            <label className="field">
              <span className="label">Your domain</span>
              <div className="settings__row">
                <input
                  type="text"
                  placeholder="docs.example.com"
                  value={domain}
                  disabled={!mayChange}
                  onChange={(e) => setDomain(e.target.value)}
                />
                <button
                  className="btn"
                  disabled={
                    !mayChange ||
                    busy !== null ||
                    (domain.trim() || null) === (domainStatus?.domain ?? null)
                  }
                  onClick={() => {
                    setBusy('domain')
                    setSaid(null)
                    void api
                      .claimDomain(domain.trim() || null)
                      .then(loadDomain)
                      .then(() =>
                        setSaid({
                          name: 'domain',
                          ok: true,
                          text: domain.trim()
                            ? 'Publish the record below, then check it.'
                            : 'Domain removed.',
                        }),
                      )
                      .catch((e: unknown) =>
                        setSaid({
                          name: 'domain',
                          ok: false,
                          text: e instanceof Error ? e.message : String(e),
                        }),
                      )
                      .finally(() => setBusy(null))
                  }}
                >
                  {busy === 'domain' ? <Icon name="spinner" size={13} /> : null}
                  Save domain
                </button>
              </div>
            </label>

            {domainStatus?.state === 'verified' && (
              <p className="screen__note">
                <Icon name="check" size={13} /> {domainStatus.domain} is proved to be yours. A
                rebuild is on its way, because the address a site answers on is written into its
                build — until it finishes, readers still reach the old one. A certificate is issued
                for it automatically, within a few minutes of the address record below being found.
              </p>
            )}

            {/*
          The state a customer spends the longest in, and the one that said
          nothing. Somebody who saved a hostname, added the record and came back
          the next day saw the instructions again with no statement of where
          things stood — and a screen that will not say is a screen somebody
          presses buttons on to find out.
        */}
            {domainStatus?.state === 'pending' && (
              <p className="home__quiet">
                Waiting for the record. Readers still reach this site at its current address, and
                nothing changes for them until the record is found.
              </p>
            )}

            {domainStatus?.domain && (
              <>
                {domainStatus.records.length > 0 && (
                  <p className="home__quiet">
                    Add {domainStatus.records.length > 1 ? 'these records' : 'this record'} where{' '}
                    {domainStatus.domain} is managed. Only somebody who controls the domain can,
                    which is what makes it proof.
                  </p>
                )}
                {/*
              One list per record, each captioned with what it is FOR.
              Two records with no captions is a screen where somebody publishes
              the proof, sees a second row of the same three words, and assumes
              they have already done it.
            */}
                {domainStatus.records.map((record) => (
                  <div key={`${record.type}-${record.name}`}>
                    <p className="home__quiet">
                      {record.purpose === 'ownership'
                        ? 'Proves the domain is yours. It can be deleted once it has been found.'
                        : 'Sends readers here. This one stays published for as long as the site answers.'}
                    </p>
                    <dl className="settings__facts">
                      <dt>Type</dt>
                      <dd>
                        <code>{record.type}</code>
                      </dd>
                      <dt>Name</dt>
                      <dd>
                        <code>{record.name}</code>
                      </dd>
                      <dt>Value</dt>
                      <dd>
                        <code>{record.value}</code>
                      </dd>
                    </dl>
                  </div>
                ))}
                <div className="settings__row">
                  <button
                    className="btn"
                    disabled={busy !== null}
                    onClick={() => {
                      setBusy('verify')
                      setSaid(null)
                      void api
                        .verifyDomain()
                        /*
                         * The answer itself, not another fetch. The address and the
                         * certificate are looked up while that request runs and are
                         * not stored anywhere — asking again returns a status with
                         * both missing, which is a check that teaches nothing.
                         */
                        .then(setDomainStatus)
                        .catch((e: unknown) =>
                          setSaid({
                            name: 'domain',
                            ok: false,
                            text: e instanceof Error ? e.message : String(e),
                          }),
                        )
                        .finally(() => setBusy(null))
                    }}
                  >
                    {busy === 'verify' ? (
                      <Icon name="spinner" size={13} />
                    ) : (
                      <Icon name="check" size={13} />
                    )}
                    Check the record
                  </button>
                  {domainStatus.checkedAt && (
                    <span className="home__quiet">
                      Last checked {new Date(domainStatus.checkedAt).toLocaleString()}
                    </span>
                  )}
                </div>
                {domainStatus.problem && (
                  <p className="screen__warn screen__warn--problem">
                    <Icon name="alert" size={13} /> {domainStatus.problem}
                  </p>
                )}
                {/*
              What a check found, when it found it. Absent from a fresh page,
              because nothing has looked: DNS and a TLS handshake are the
              world's answer rather than a stored one, and inventing a stale
              one here is the failure this whole card exists to avoid.
            */}
                {domainStatus.address?.ok && (
                  <p className="screen__note">
                    <Icon name="check" size={13} /> {domainStatus.domain} points here.
                  </p>
                )}
                {domainStatus.certificate?.ok && (
                  <p className="screen__note">
                    <Icon name="check" size={13} /> HTTPS is answering
                    {domainStatus.certificate.expiresAt
                      ? `, with a certificate valid until ${new Date(
                          domainStatus.certificate.expiresAt,
                        ).toLocaleDateString()}`
                      : ''}
                    . It renews by itself.
                  </p>
                )}
                {/*
              What the certificate reconciler last did for THIS hostname, read
              through the workspace's own view of its row. A stored fact,
              unlike the two probes above, and said as one: when it will try
              again, or why it did not. Nothing for a certificate that is
              simply there — the handshake above already says so.
            */}
                {domainStatus.provisioning?.outcome === 'waiting' && (
                  <p className="screen__note" data-certificate-state="waiting">
                    A certificate was tried for {domainStatus.provisioning.hostname} and did not
                    arrive
                    {domainStatus.provisioning.problem
                      ? ` — ${domainStatus.provisioning.problem}`
                      : ''}
                    . The next attempt is after{' '}
                    {domainStatus.provisioning.nextAt
                      ? new Date(domainStatus.provisioning.nextAt).toLocaleString()
                      : 'a short wait'}
                    . Nothing to do unless the records above are wrong.
                  </p>
                )}
                {domainStatus.provisioning?.outcome === 'refused' && (
                  <p className="screen__note" data-certificate-state="refused">
                    A certificate was not requested for {domainStatus.provisioning.hostname}
                    {domainStatus.provisioning.problem
                      ? `: ${domainStatus.provisioning.problem}`
                      : ''}
                    . It is looked at again within a few minutes of the records above being right.
                  </p>
                )}
                {domainStatus.provisioning?.outcome === 'failed' && (
                  <p className="screen__note" data-certificate-state="failed">
                    The last attempt to issue a certificate for {domainStatus.provisioning.hostname}{' '}
                    failed
                    {domainStatus.provisioning.problem
                      ? ` — ${domainStatus.provisioning.problem}`
                      : ''}
                    . It is tried again, with growing waits.
                  </p>
                )}
                {domainStatus.provisioning?.outcome === 'issued' &&
                  !domainStatus.certificate?.ok && (
                    <p className="screen__note" data-certificate-state="issued">
                      <Icon name="check" size={13} /> A certificate was issued for{' '}
                      {domainStatus.provisioning.hostname} on{' '}
                      {new Date(domainStatus.provisioning.runAt).toLocaleDateString()}.
                    </p>
                  )}
              </>
            )}
          </Card>

          <Card at="site/discovery" here={where} data-discovery-card>
            <h2>Discovery files</h2>
            <p className="home__quiet">
              These are generated with the published site. Search engines, feed readers and
              language-model tools can read them without signing in; drafts, private pages and pages
              marked noindex stay out of the listed files.
            </p>
            {publishedBase ? (
              <>
                <div className="discovery__home">
                  <span>
                    Published site
                    <strong>{publishedBase}</strong>
                  </span>
                  <a
                    className="btn btn--sm btn--outline"
                    href={publishedBase}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Icon name="link" size={13} />
                    Open site
                  </a>
                </div>
                <div className="discovery__grid">
                  {discoveryLinks.map((link) => (
                    <a
                      key={link.path}
                      className="discovery__link"
                      href={link.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <span className="discovery__label">
                        <Icon name={link.path.endsWith('.xml') ? 'code' : 'page'} size={15} />
                        {link.label}
                      </span>
                      <code>{link.path}</code>
                      <span>{link.says}</span>
                    </a>
                  ))}
                </div>
                <p className="field__note">
                  API descriptions are source files, not generated discovery files. Configure the
                  OpenAPI path in API reference when this site publishes one.
                </p>
              </>
            ) : (
              <p className="nothing">
                This deployment has not reported a published site address for this project yet.
              </p>
            )}
          </Card>

          {/* A stable hook for the gates, like the webhooks card has: the
          controls inside change their words — "Upload" becomes "Replace" the
          moment there is something to replace — so a test that finds the card
          by a button's label stops finding it exactly when it matters. */}
          <Card
            at="site/appearance"
            here={where}
            said={said}
            owns={[
              'accent',
              'announcement',
              'custom-css',
              'icon',
              'icon-clear',
              'logo',
              'logo-clear',
              'social',
              'social-clear',
              'cta',
              'upgrade',
              /* The look itself is filed per preset — see `ThemePresets`. */
              'preset-',
            ]}
            data-theme-card
          >
            <h2>Theme</h2>
            <p className="home__quiet">
              Your brand on your published site. Saving rebuilds it, so a change reaches readers
              within a minute or two rather than waiting for the next page you publish. A draft’s
              preview link builds with the theme chosen here, so you can see a change before your
              readers do.
            </p>
            <ThemePresets
              value={settings.theme.preset}
              busy={busy}
              disabled={!mayChange}
              onChoose={(id) =>
                void act(
                  `preset-${id}`,
                  () => api.updateTheme({ preset: id }),
                  `${THEME_PRESETS.find((p) => p.id === id)?.label ?? id} will appear on the site’s next publish. Open a draft’s preview to see it now.`,
                )
              }
            />
            {/*
          The code theme.

          A menu rather than the preset's cards: it is one line of the look,
          not a whole one, and five cards beside four cards is a screen that
          asks a person to compare two grids. Each option says what it looks
          like, because "Vivid" alone is a guess.

          Every name here was built and measured in a browser before it was
          offered — see packages/schema/src/code-themes.ts, which records the
          numbers and the four themes that qualified and were left out anyway.
        */}
            {/*
          The type this documentation is set in.

          BRAND.md §8 has always named the two font families as something a
          theme may change; what kept it off the screen was the rule beside it —
          a documentation platform that blocks its customers' pages on a request
          to Google Fonts advertises the opposite of what it sells, and
          PRIVACY.md §6 promises no third-party fonts on a published site. All
          four faces here are self-hosted and already in the build.

          A pairing rather than two menus: the interesting decision is how the
          prose and the code sit together on one page, and offering somebody a
          way to pair a humanist sans with a geometric mono is offering them a
          way to make their documentation worse.
        */}
            <label className="field" data-fonts>
              <span className="label">Type</span>
              <select
                className="input"
                value={settings.theme.fonts ?? DEFAULT_FONTS}
                disabled={!mayChange || busy !== null}
                onChange={(e) => {
                  const id = e.target.value
                  void act(
                    `fonts-${id}`,
                    () => api.updateTheme({ fonts: id }),
                    `${FONT_PAIRINGS.find((p) => p.id === id)?.label ?? id} will appear on the site’s next publish. Open a draft’s preview to see it now.`,
                  )
                }}
              >
                {FONT_PAIRINGS.map((pairing) => (
                  <option key={pairing.id} value={pairing.id}>
                    {pairing.label}
                  </option>
                ))}
              </select>
            </label>
            <p className="field__note">
              {FONT_PAIRINGS.find((p) => p.id === (settings.theme.fonts ?? DEFAULT_FONTS))
                ?.character ?? ''}
            </p>

            <label className="field" data-code-theme>
              <span className="label">Code blocks</span>
              <select
                className="input"
                value={settings.theme.codeTheme ?? DEFAULT_CODE_THEME}
                disabled={!mayChange || busy !== null}
                onChange={(e) => {
                  const id = e.target.value
                  void act(
                    `code-theme-${id}`,
                    () => api.updateTheme({ codeTheme: id }),
                    `${CODE_THEMES.find((t) => t.id === id)?.label ?? id} will appear on the site’s next publish. Open a draft’s preview to see it now.`,
                  )
                }}
              >
                {CODE_THEMES.map((theme) => (
                  <option key={theme.id} value={theme.id}>
                    {theme.label}
                  </option>
                ))}
              </select>
            </label>
            <p className="field__note">
              {CODE_THEMES.find((t) => t.id === (settings.theme.codeTheme ?? DEFAULT_CODE_THEME))
                ?.character ?? ''}
            </p>

            {/*
          Their own CSS, for the tweak a preset cannot make.

          Scoped by the service to the documentation, so it cannot repaint the
          navigation into something unusable on a phone — the change a customer
          cannot test as thoroughly as we can. Refusals come back as a sentence
          naming what was wrong, because a stylesheet that half-applies is
          debugged for an hour.
        */}
            {settings.plan.customCss ? (
              <>
                <label className="field" data-custom-css>
                  <span className="label">Your own CSS</span>
                  <textarea
                    className="input mono"
                    rows={5}
                    placeholder={'table { font-size: 14px }'}
                    aria-label="Your own CSS"
                    value={customCss}
                    disabled={!mayChange}
                    onChange={(event) => setCustomCss(event.target.value)}
                  />
                </label>
                <div className="settings__row">
                  <button
                    className="btn btn--tinted btn--sm"
                    data-save-custom-css
                    disabled={!mayChange || busy !== null}
                    onClick={() =>
                      void act(
                        'custom-css',
                        async () => {
                          await api.updateTheme({
                            customCss: customCss.trim() === '' ? null : customCss,
                          })
                          await load()
                        },
                        customCss.trim() === ''
                          ? 'Your CSS is cleared.'
                          : 'Saved. It applies to your documentation, not to the navigation.',
                      )
                    }
                  >
                    {busy === 'custom-css' ? (
                      <Icon name="spinner" size={13} />
                    ) : (
                      <Icon name="check" size={13} />
                    )}
                    Save CSS
                  </button>
                  <span className="field__note">
                    Applies to your pages. `@import` and another server’s `url()` are refused —
                    upload the file instead.
                  </span>
                </div>
              </>
            ) : (
              /*
            Not offered, rather than offered and refused.

            The field used to be here on every plan, and the 402 arrived at
            Save — after somebody had written a stylesheet. Saying it is paid
            BEFORE the work is the difference between a product with a rule and
            a product that looks broken. It still names what this plan does
            include, the same way the route's refusal does: the point is to be
            told where you stand, not to be sold to.
          */
              <div className="field" data-custom-css-paid>
                <span className="label">Your own CSS</span>
                <p className="field__note">
                  Part of a paid plan; this workspace is on {settings.plan.label}. The five themes,
                  the accent and the code theme above are included here.
                </p>
                {/*
              And a way to act on it, for the person who can.

              Owner or billing access: money is not implied by admin, and
              offering a button that answers 403 is the thing this card exists
              to stop. Absent when the deployment has no billing configured,
              for the same reason.
            */}
                {/*
              An unconfirmed address cannot start a subscription (§5), so the
              button says why instead of answering 403 after the press. The
              banner at the top of every screen is how it gets confirmed; this
              only has to explain why the upgrade is not here.
            */}
                {mayManageBilling &&
                  settings.billingConfigured &&
                  settings.plan.plan === 'free' &&
                  settings.emailConfirmed === false && (
                    <p className="field__note" data-upgrade-unconfirmed>
                      Confirm your email address first — receipts and any payment problem go to it.
                      The banner at the top of the screen sends the link.
                    </p>
                  )}
                {mayManageBilling &&
                  settings.billingConfigured &&
                  settings.plan.plan === 'free' &&
                  settings.emailConfirmed !== false && (
                    <button
                      className="btn btn--tinted btn--sm"
                      data-upgrade
                      disabled={busy !== null}
                      onClick={() =>
                        void act(
                          'upgrade',
                          async () => {
                            const { url } = await api.startUpgrade()
                            // Stripe's own page, in this tab: a payment form in a
                            // new window is a payment form a pop-up blocker eats.
                            window.location.assign(url)
                          },
                          'Taking you to Stripe…',
                        )
                      }
                    >
                      {busy === 'upgrade' ? (
                        <Icon name="spinner" size={13} />
                      ) : (
                        <Icon name="plus" size={13} />
                      )}
                      Upgrade to Pro
                    </button>
                  )}
              </div>
            )}

            <div className="settings__row">
              <span
                className="theme__swatch"
                style={HEX_COLOUR.test(accent.trim()) ? { background: accent.trim() } : undefined}
                aria-hidden="true"
              />
              <input
                type="text"
                className="theme__accent-text"
                placeholder="#2563eb"
                value={accent}
                disabled={!mayChange}
                onChange={(e) => setAccent(e.target.value)}
              />
              <input
                type="color"
                aria-label="Pick an accent colour"
                value={HEX_COLOUR.test(accent.trim()) ? accent.trim() : '#2563eb'}
                disabled={!mayChange}
                onChange={(e) => setAccent(e.target.value)}
              />
              <button
                className="btn"
                disabled={
                  !mayChange ||
                  busy !== null ||
                  (accent.trim().toLowerCase() || null) === settings.theme.accent
                }
                onClick={() =>
                  void act(
                    'accent',
                    () => api.updateTheme({ accent: accent.trim() || null }),
                    accent.trim()
                      ? 'The accent will appear on the site’s next publish.'
                      : 'Accent cleared. The default look returns on the next publish.',
                  )
                }
              >
                {busy === 'accent' ? <Icon name="spinner" size={13} /> : null}
                Save accent
              </button>
            </div>

            <div className="settings__row">
              <ThemeAssetPreview
                src={settings.theme.logoUrl}
                alt="Current logo"
                className="theme__logo-preview"
              />
              <input
                ref={logoInput}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                hidden
                onChange={chooseLogo}
              />
              <button
                className="btn"
                disabled={!mayChange || busy !== null}
                onClick={() => logoInput.current?.click()}
              >
                {busy === 'logo' ? (
                  <Icon name="spinner" size={13} />
                ) : (
                  <Icon name="image" size={13} />
                )}
                {settings.theme.logoUrl ? 'Replace logo' : 'Upload a logo'}
              </button>
              {settings.theme.logoUrl && (
                <button
                  className="btn btn--ghost"
                  disabled={!mayChange || busy !== null}
                  onClick={() =>
                    void act(
                      'logo-clear',
                      () => api.updateTheme({ logoKey: null }),
                      'Logo removed. The default mark returns on the next publish.',
                    )
                  }
                >
                  Remove logo
                </button>
              )}
            </div>

            {/*
          The browser-tab icon.

          Until now every published site carried OURS — a customer on their own
          domain, with their own accent and their own logo in the header, had
          the Staleless badge in every tab their readers opened. A project that
          uploads none now ships a plain page glyph instead, which says
          "documentation" and nothing about who built it.
        */}
            <div className="settings__row" data-favicon>
              <ThemeAssetPreview
                src={settings.theme.faviconUrl}
                alt="Current tab icon"
                className="theme__icon-preview"
                iconSize={14}
              />
              <input
                ref={iconInput}
                type="file"
                accept="image/png,image/svg+xml,image/x-icon,image/vnd.microsoft.icon"
                hidden
                onChange={chooseIcon}
              />
              <button
                className="btn"
                disabled={!mayChange || busy !== null}
                onClick={() => iconInput.current?.click()}
              >
                {busy === 'icon' ? (
                  <Icon name="spinner" size={13} />
                ) : (
                  <Icon name="image" size={13} />
                )}
                {settings.theme.faviconUrl ? 'Replace tab icon' : 'Upload a tab icon'}
              </button>
              {settings.theme.faviconUrl && (
                <button
                  className="btn btn--ghost"
                  disabled={!mayChange || busy !== null}
                  onClick={() =>
                    void act(
                      'icon-clear',
                      () => api.updateTheme({ faviconKey: null }),
                      'Tab icon removed. A plain page glyph returns on the next publish.',
                    )
                  }
                >
                  Remove tab icon
                </button>
              )}
            </div>
            <p className="field__note">
              Square, and legible at sixteen pixels — a browser draws it very small. A PNG, SVG or
              ICO.
            </p>

            {/*
          The image a link to this site previews with.

          Every site used to point at one card committed to this repository and
          shared by all of them, which carried whatever name it was last
          rendered for. A site with none now advertises no image at all: a link
          with no picture is a much smaller loss than a link wearing somebody
          else's name.
        */}
            <div className="settings__row" data-social-image>
              <ThemeAssetPreview
                src={settings.theme.socialImageUrl}
                alt="Current preview image"
                className="theme__social-preview"
                iconSize={18}
              />
              <input
                ref={socialInput}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                hidden
                onChange={chooseSocialImage}
              />
              <button
                className="btn"
                disabled={!mayChange || busy !== null}
                onClick={() => socialInput.current?.click()}
              >
                {busy === 'social' ? (
                  <Icon name="spinner" size={13} />
                ) : (
                  <Icon name="image" size={13} />
                )}
                {settings.theme.socialImageUrl ? 'Replace preview image' : 'Upload a preview image'}
              </button>
              {settings.theme.socialImageUrl && (
                <button
                  className="btn btn--ghost"
                  disabled={!mayChange || busy !== null}
                  onClick={() =>
                    void act(
                      'social-clear',
                      () => api.updateTheme({ socialImageKey: null }),
                      'Preview image removed. Links to this site will show no picture.',
                    )
                  }
                >
                  Remove preview image
                </button>
              )}
            </div>
            <p className="field__note">
              Shown when somebody shares a link to this site — in a chat, on a social platform. 1200
              by 630 is what every platform crops to. A PNG, JPEG or WebP.
            </p>

            {/*
          The line above every page.

          Plain words and an optional link — no markdown, no HTML. A banner
          that could carry markup would be a customer-authored script tag on
          every page of their own site, and a banner is one sentence, so
          nothing is lost by saying it cannot contain one.

          No expiry date, though it is the obvious next thought: a static site
          cannot forget a banner at midnight, because nothing runs. "It
          disappears on the 1st" would be a promise the architecture cannot
          keep.
        */}
            <div className="settings__row">
              <label className="field">
                <span className="label">Announcement</span>
                <input
                  className="input"
                  placeholder="Version 2 is out"
                  maxLength={160}
                  value={banner}
                  disabled={!mayChange}
                  onChange={(e) => setBanner(e.target.value)}
                />
              </label>
              <label className="field">
                <span className="label">Links to (optional)</span>
                <input
                  className="input mono"
                  placeholder="/guides/upgrade"
                  value={bannerHref}
                  disabled={!mayChange}
                  onChange={(e) => setBannerHref(e.target.value)}
                />
              </label>
              <button
                className="btn"
                disabled={!mayChange || busy !== null}
                onClick={() =>
                  void act(
                    'announcement',
                    () =>
                      api.setAnnouncement(
                        banner.trim()
                          ? { text: banner.trim(), href: bannerHref.trim() || null }
                          : null,
                      ),
                    banner.trim()
                      ? 'Saved. The site is rebuilding, so readers see it in a minute or two.'
                      : 'Announcement removed. The site is rebuilding without it.',
                  )
                }
              >
                {busy === 'announcement' ? <Icon name="spinner" size={13} /> : null}
                {banner.trim() ? 'Save announcement' : 'Remove announcement'}
              </button>
            </div>
            <p className="field__note">
              One line above every page — a release, a move, a warning. It appears on the next
              publish, and it stays until you take it down.
            </p>

            <div className="settings__row">
              <label className="field field--checkbox">
                <input
                  type="checkbox"
                  checked={ctaEnabled}
                  disabled={!mayChange}
                  onChange={(e) => setCtaEnabled(e.target.checked)}
                />
                <span className="label">Header CTA</span>
              </label>
              <label className="field">
                <span className="label">Button label</span>
                <input
                  className="input"
                  placeholder="Get started"
                  maxLength={80}
                  value={ctaLabel}
                  disabled={!mayChange || !ctaEnabled}
                  onChange={(e) => setCtaLabel(e.target.value)}
                />
              </label>
              <label className="field">
                <span className="label">Button link</span>
                <input
                  className="input mono"
                  placeholder="https://your-product.com/signup"
                  value={ctaHref}
                  disabled={!mayChange || !ctaEnabled}
                  onChange={(e) => setCtaHref(e.target.value)}
                />
              </label>
              <button
                className="btn"
                disabled={!mayChange || busy !== null || (ctaEnabled && !ctaHref.trim())}
                onClick={() =>
                  void act(
                    'cta',
                    () =>
                      api.updateTheme({
                        ctaEnabled,
                        ctaLabel: ctaEnabled ? ctaLabel.trim() || 'Get started' : null,
                        ctaHref: ctaEnabled ? ctaHref.trim() : null,
                      }),
                    ctaEnabled
                      ? 'Header CTA saved. The site is rebuilding, so readers see it in a minute or two.'
                      : 'Header CTA removed. The site is rebuilding without it.',
                  )
                }
              >
                {busy === 'cta' ? <Icon name="spinner" size={13} /> : null}
                {ctaEnabled ? 'Save CTA' : 'Remove CTA'}
              </button>
            </div>
            <p className="field__note">
              Optional button in the top-right of this documentation site only. Use a site path or
              an https:// URL. It is hidden until you enable it and save.
            </p>
          </Card>

          {/*
        The setting that makes the API-change inbox possible.

        The comparison — an OpenAPI diff turned into "these pages describe
        something that changed" — was complete and reachable only from a
        command line with two files named on it, so the inbox's `api-change`
        trigger was a queue that could never receive anything. This is where a
        person points at their file.
      */}
          <Card at="site/api" here={where} said={said} owns={['reference']} data-api-spec>
            <h2>API reference</h2>
            <p className="home__quiet">
              Commit your OpenAPI file beside your documentation, and every publish compares it with
              the last one we saw. What changed becomes suggestions naming the pages that describe
              it — breaking changes first, and an endpoint nobody has documented at all above a
              documented one that merely moved.
            </p>
            <p className="home__quiet">
              We look for <code>openapi.json</code> and <code>openapi.yaml</code> at the top of your
              repository, and under <code>api/</code> or <code>spec/</code>. Say where it is if it
              is somewhere else.
            </p>
            {specError && (
              <p className="screen__error" role="alert">
                <Icon name="alert" size={13} /> {specError}
              </p>
            )}
            {/*
          The other half of connecting a specification.
          Reading it and never writing anything left a customer hand-writing
          every endpoint, which for an API of any size is the work they bought
          a documentation tool to avoid.
        */}
            <div className="settings__row">
              <button
                className="btn btn--tinted btn--sm"
                disabled={!mayChange || busy !== null}
                onClick={() =>
                  void act(
                    'reference',
                    async () => {
                      const out = await api.generateApiReference()
                      setReference(out)
                    },
                    'Reference pages are on a draft. Read them, then publish when they are right.',
                  )
                }
              >
                {busy === 'reference' ? (
                  <Icon name="spinner" size={13} />
                ) : (
                  <Icon name="page" size={13} />
                )}
                Write reference pages
              </button>
              <span className="field__note">
                A page per endpoint, grouped by tag, onto a draft — never straight onto the live
                site.
              </span>
            </div>
            {reference && (
              <p className="home__quiet">
                {reference.written.length} page{reference.written.length === 1 ? '' : 's'} written
                to <code>{reference.branch}</code> from <code>{reference.source}</code>
                {reference.kept.length > 0 &&
                  `, and ${reference.kept.length} left alone because somebody had edited ${reference.kept.length === 1 ? 'it' : 'them'}`}
                {reference.unchanged.length > 0 &&
                  `, ${reference.unchanged.length} already up to date`}
                {reference.placed.length > 0 &&
                  `, ${reference.placed.length} added to the navigation`}
                .{reference.notes.length > 0 && ` ${reference.notes.join(' ')}`}
              </p>
            )}
            {/*
          What is actually connected, which the field above cannot say.

          A project does not have to name its file — the engine looks in the
          usual places — so the field is empty for most projects that HAVE a
          description, and an empty field reads as "nothing is connected" to
          somebody whose specification is already being compared after every
          publish. Found while building the Home card on the same feature: the
          setting was null on a workspace whose inbox was full of suggestions
          from a file it had found by itself.
        */}
            {specFound && (
              <p className="field__note" data-api-spec-found>
                {specFound.resolved === null ? (
                  <>
                    No API description found. We look for{' '}
                    {specFound.usualPlaces.slice(0, 3).map((place, at) => (
                      <span key={place}>
                        {at > 0 && ', '}
                        <code>{place}</code>
                      </span>
                    ))}{' '}
                    and a few more; commit one, or name yours below.
                  </>
                ) : specFound.configured ? (
                  <>
                    Using <code>{specFound.resolved}</code>, because this project named it.
                  </>
                ) : (
                  <>
                    Using <code>{specFound.resolved}</code>, found beside your documentation. Name a
                    different file below to override it.
                  </>
                )}
              </p>
            )}
            {/*
          What is wrong with the file, and WHERE.

          A broken `$ref` or an operation with no responses shows up as a thin
          or wrong generated page, and then the customer has to guess which
          endpoint. Errors and notes are kept apart on purpose: an error is the
          document contradicting itself, a note is writing that is missing, and
          calling the second an error is how a list of problems gets ignored.
        */}
            {specFound && specFound.findings.length > 0 && (
              <div className="field__note" data-api-spec-findings>
                <p>
                  {specFound.findings.filter((f) => f.severity === 'error').length} problem
                  {specFound.findings.filter((f) => f.severity === 'error').length === 1
                    ? ''
                    : 's'}{' '}
                  and {specFound.findings.filter((f) => f.severity === 'note').length} thin endpoint
                  {specFound.findings.filter((f) => f.severity === 'note').length === 1
                    ? ''
                    : 's'}{' '}
                  in that file:
                </p>
                <ul className="settings__list">
                  {specFound.findings.slice(0, 8).map((finding) => (
                    <li key={`${finding.at}:${finding.says}`} data-severity={finding.severity}>
                      <code>{finding.at || 'the whole file'}</code> — {finding.says}
                    </li>
                  ))}
                </ul>
                {specFound.findings.length > 8 && <p>and {specFound.findings.length - 8} more.</p>}
              </div>
            )}
            <div className="settings__row">
              <label className="field">
                <span className="label">Path in your repository</span>
                <input
                  className="input mono"
                  placeholder="openapi.json"
                  aria-label="OpenAPI file"
                  value={specPath}
                  disabled={!mayChange}
                  onChange={(e) => setSpecPath(e.target.value)}
                />
              </label>
              <button
                className="btn btn--sm btn--outline"
                disabled={!mayChange || specBusy}
                onClick={() => void saveSpecPath()}
              >
                Save
              </button>
            </div>
          </Card>

          {/*
        The one control in the product that sends a customer's content anywhere
        near a model.

        Its own card, above Storage rather than beside the theme, because it is
        not a matter of taste: turning it on means this site's passages and its
        readers' questions leave the deployment. So the card says who receives
        them, what is kept, and that the switch is the only thing that starts
        it — the same three facts as docs/PRIVACY.md §4, in the place where the
        decision is actually taken.

        Owner or admin of the WORKSPACE, matching the route. A writer with the
        run of one site does not get to decide this for the company.
      */}
          <Card
            at="site/general"
            here={where}
            said={said}
            owns={['ai-cap', 'ask', 'ask-log', 'writing-help']}
            data-ask
          >
            <h2>Help from a model</h2>
            <p className="home__quiet">
              Readers can ask your documentation a question and get an answer, with links to the
              pages it came from. The answer uses only what your pages say: when they do not cover
              it, the reply is that it could not be found here, and an answer that cannot point at a
              page is not shown at all.
            </p>
            <p className="home__quiet">
              This is off until you turn it on. While it is on, a question and up to six matching
              passages of this site’s published documentation go to our model provider, OpenRouter,
              to be answered. The question is thrown away afterwards unless you keep it below.
              Nothing else about your reader is sent, private pages and drafts are never used, and
              turning it off stops it at the next question.
            </p>
            {/*
          A private site cannot answer, and saying so here is better than
          letting somebody turn a switch that does nothing: `POST /ask` refuses
          a site that is not public, because it cannot tell a reader who passed
          the gate from one who did not.
        */}
            {settings.visibility !== 'public' && (
              <p className="field__note" data-ask-private>
                This site is private, so it cannot answer readers’ questions. The switch below is
                kept for when it is public again.
              </p>
            )}
            <label className="settings__row">
              <input
                type="checkbox"
                data-ask-enabled
                checked={askEnabled}
                disabled={!mayDecideAsk || busy !== null}
                onChange={(e) => {
                  const wanted = e.target.checked
                  setAskEnabled(wanted)
                  void act(
                    'ask',
                    /**
                     * Put the switch back if the write is refused. `act` reports
                     * the failure and does not reload — so without this the box
                     * would sit in a state the server never agreed to.
                     */
                    () =>
                      api.setAskEnabled(wanted).catch((error: unknown) => {
                        setAskEnabled(!wanted)
                        throw error
                      }),
                    wanted
                      ? 'Ask is on. Rebuild the published site so readers can see it.'
                      : 'This site no longer answers questions.',
                  )
                }}
              />
              <span>Let readers ask this site questions</span>
            </label>
            {/*
          Keeping the questions is a third decision, and a different promise:
          answering sends a reader's question to a provider and forgets it,
          keeping it puts their words in this workspace's database. Offered only
          when answering is on, because there would be nothing to keep.
        */}
            {askEnabled && (
              <>
                <div className="settings__row settings__row--action">
                  <div>
                    <strong>Publish this setting to the site</strong>
                    <span className="field__note">
                      Ask is part of the static published site, so readers see this switch after a
                      rebuild.
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn btn--outline"
                    data-rebuild-site
                    disabled={!settings.you?.canPublish || busy !== null}
                    onClick={() =>
                      void act(
                        'rebuild-site',
                        () => api.rebuildSite(),
                        'The published site rebuild is queued.',
                      )
                    }
                  >
                    {busy === 'rebuild-site' ? 'Queuing…' : 'Rebuild published site'}
                  </button>
                </div>
                <label className="settings__row">
                  <input
                    type="checkbox"
                    data-ask-log
                    checked={askLog}
                    disabled={!mayDecideAsk || busy !== null}
                    onChange={(e) => {
                      const wanted = e.target.checked
                      setAskLog(wanted)
                      void act(
                        'ask-log',
                        () =>
                          api.setAskLogEnabled(wanted).catch((error: unknown) => {
                            setAskLog(!wanted)
                            throw error
                          }),
                        wanted
                          ? 'Questions readers ask will be kept, and shown on Analytics.'
                          : 'No new questions will be kept. The ones already there go on the ' +
                              'retention window Analytics sets.',
                      )
                    }}
                  />
                  <span>
                    Keep the questions readers ask, so you can see what they want to know
                    <span className="field__note">
                      Your readers are told: the Ask box says their question is kept, instead of
                      saying it is not. Turning this off stops new ones; what is already kept leaves
                      with the rest of the reader analytics, on the window that screen sets.
                    </span>
                  </span>
                </label>
              </>
            )}

            {/*
          The second switch, in the same card because it is the same decision —
          whether this project's words may leave for a model — and a different
          one because the words are different. Ask sends PUBLISHED passages to
          answer a stranger; this sends whatever a writer has highlighted,
          including a draft nobody has seen.
        */}
            <p className="home__quiet">
              Writers can also ask for help with the words themselves — improve, shorten, simplify,
              fix the grammar, translate, draft from notes — on the text they have selected. A
              suggestion is shown beside what they wrote and never applied on its own. What is sent
              is the selection, the glossary terms it uses and this site’s style guide, so a
              suggestion keeps the words and the voice this documentation has decided on; nothing
              else goes.
            </p>
            <label className="settings__row">
              <input
                type="checkbox"
                data-writing-help
                checked={writingHelp}
                disabled={!mayDecideAsk || busy !== null}
                onChange={(e) => {
                  const wanted = e.target.checked
                  setWritingHelp(wanted)
                  void act(
                    'writing-help',
                    () =>
                      api.setWritingHelpEnabled(wanted).catch((error: unknown) => {
                        setWritingHelp(!wanted)
                        throw error
                      }),
                    wanted
                      ? 'Writers here can ask for help with the words.'
                      : 'Writers here can no longer ask for help with the words.',
                  )
                }}
              />
              <span>Let writers ask for help with the words</span>
            </label>
            {/*
          §55: "transparent usage, thresholds, hard cap, no surprise billing".
          The cap has existed since the features did and nobody could see it — a
          writer met it as a refusal at four in the afternoon. Both numbers come
          from the counters the routes enforce, so this cannot drift from the
          rule, and the sentence says plainly that no bill follows.
        */}
            {usage?.configured && (usage.writing.on || usage.ask.on) && (
              <div className="settings__facts" data-ai-usage>
                {usage.writing.on && (
                  <>
                    <dt>Help with the words, today</dt>
                    <dd>
                      {usage.writing.project.used.toLocaleString()} of{' '}
                      {usage.writing.project.limit.toLocaleString()} for this site
                      {usage.writing.project.used >= usage.writing.project.limit * 0.8 && (
                        <span className="badge badge--warn" data-ai-usage-warn>
                          close to the day’s limit
                        </span>
                      )}
                      <span className="field__note">
                        {usage.writing.you.used.toLocaleString()} of{' '}
                        {usage.writing.you.limit.toLocaleString()} of them yours.
                      </span>
                    </dd>
                  </>
                )}
                {usage.ask.on && (
                  <>
                    <dt>Readers’ questions, today</dt>
                    <dd>
                      {usage.ask.site.used.toLocaleString()} of{' '}
                      {usage.ask.site.limit.toLocaleString()} answered
                      {usage.ask.site.used >= usage.ask.site.limit * 0.8 && (
                        <span className="badge badge--warn" data-ai-usage-warn>
                          close to the day’s limit
                        </span>
                      )}
                      <span className="field__note">
                        Past that, a reader is told the site has answered many questions today and
                        asked to come back later.
                      </span>
                    </dd>
                  </>
                )}
                {/*
              One sentence about money, covering both lines, because it is one
              fact: §55 asks for "no surprise billing", and the honest version
              today is that these limits are the whole billing story.

              And whose limits they are. The numbers above come from the plan,
              so a meter reading "48 of 50" without naming the plan leaves
              somebody guessing whether paying would change it — which is
              exactly the question a meter near its limit provokes. Said here
              once rather than beside each number: it is the same answer for
              both.
            */}
                <dt>Cost</dt>
                <dd>
                  These daily limits exist so nothing here can run up a bill. You are not charged
                  for what a model answers, and both limits start again every day.
                  <span className="field__note" data-ai-usage-plan>
                    {usage.plan === 'Free'
                      ? 'These are the Free limits. A paid plan allows more each day — see Plan above.'
                      : `These are the ${usage.plan} limits.`}
                  </span>
                </dd>
                {/*
              And the ceiling an OWNER can put under it (§55: "owner-configured
              hard cap"). Beside the meters, because it is the number they
              report — a field somewhere else on the screen would be a rule
              nobody connects to what they are reading.

              Zero is allowed and says what it means: nothing is asked of a
              model, while the features stay on. That is a different decision
              from the switches above, which say whether the feature exists.
            */}
                {mayDecideAsk && (
                  <>
                    <dt>Your own limit</dt>
                    <dd>
                      <div className="settings__row">
                        <input
                          className="input input--short"
                          type="number"
                          min={0}
                          step={1}
                          aria-label="Calls a day, at most"
                          placeholder={String(usage.writing.project.limit)}
                          value={cap}
                          disabled={busy !== null}
                          onChange={(event) => setCap(event.target.value)}
                        />
                        <button
                          className="btn btn--tinted btn--sm"
                          data-save-ai-cap
                          disabled={busy !== null}
                          onClick={() =>
                            void act(
                              'ai-cap',
                              async () => {
                                const wanted = cap.trim() === '' ? null : Math.floor(Number(cap))
                                if (wanted !== null && (!Number.isFinite(wanted) || wanted < 0)) {
                                  throw new Error('Give a whole number of calls a day, or nothing.')
                                }
                                await api.setAiCap(wanted)
                                await loadUsage()
                              },
                              cap.trim() === ''
                                ? 'This site uses what its plan includes.'
                                : `This site will stop after ${Math.floor(Number(cap))} of each a day.`,
                            )
                          }
                        >
                          Save
                        </button>
                      </div>
                      <span className="field__note">
                        At most this many suggestions a day, and at most this many readers’
                        questions — each counted on its own. Leave it empty to use what the plan
                        includes; it can only lower the numbers above, never raise them.
                      </span>
                    </dd>
                  </>
                )}
              </div>
            )}

            {!mayDecideAsk && (
              <p className="field__note">
                An owner or an admin of this workspace decides this, because it decides where this
                site’s content goes.
              </p>
            )}
          </Card>

          {/*
        Where the documentation lives, before the card that says where it is
        KEPT. The two are the same question at different depths — this one is
        the customer's decision, Storage is the consequence of it — and reading
        them the other way round would have somebody deciding after being told.
      */}
          {where === 'site/repository' && <ConnectedRepository mayChange={mayChange} />}

          {/*
        And where this documentation can be reached FROM, which is the other
        half of the same question: the card above says where the pages live,
        this one says who may search them without opening the product.
      */}
          {where === 'org/chat' && <ConnectedSlack mayChange={mayChange} />}

          {/*
        And the two systems this documentation is measured AGAINST rather than
        reached from: the helpdesk whose questions say which page is missing,
        and the tracker whose shipped issues say which page has gone out of
        date. They sit after the repository and the chat card because they are
        the same question one step out — where the evidence comes from, once it
        is established where the pages live and who may search them.
      */}
          {where === 'org/chat' && <ConnectedIntegrations mayChange={mayChange} />}

          {/*
        And who may sign in at all, which is the deepest of the three: the
        repository says where the pages live, Slack says who may search them,
        and this says which directory decides who any of those people are.
        `mayDecideAsk` rather than `mayChange` — it is the WORKSPACE's identity,
        not one site's, and the route refuses a project-only admin anyway.
      */}
          {where === 'org/directory' && <ConnectedDirectory mayChange={mayDecideAsk} />}

          <Card at="site/general" here={where}>
            <h2>Storage</h2>
            <p className="home__quiet">
              Every page in this site lives in a git repository — that is what makes agents, reviews
              and history possible. You never need to touch it.
            </p>
            <dl className="settings__facts">
              <dt>Repository</dt>
              <dd>
                <code>{settings.project?.repository ?? settings.contentRepo}</code>
              </dd>
              <dt>Workspace</dt>
              <dd>
                <code>{settings.slug}</code>
              </dd>
            </dl>
          </Card>

          {mayReadWebhooks && hooks && (
            <Card at="org/webhooks" here={where} said={said} owns={['hook:']} data-webhooks>
              <h2>Webhooks</h2>
              <p className="home__quiet">
                Tell other software when this site publishes, so a marketing site can rebuild, a
                cache can clear or a build can start without anybody watching for it. Each request
                carries a signature made from the endpoint’s own secret, so the receiver can prove
                it came from us and not from somebody who guessed the address.
              </p>
              <p className="home__quiet">
                Only <code>https</code>, and only addresses on the public internet — an address
                inside this network, or a cloud metadata address, is refused by name. Redirects are
                checked again before they are followed.
              </p>

              {hookError && (
                <p className="screen__error" role="alert">
                  <Icon name="alert" size={13} /> {hookError}
                </p>
              )}

              {hookSecret && (
                /* Shown once, and there is no route that can show it again — one
               that could would turn a stolen session into the ability to forge
               events into somebody's CI system. */
                <>
                  <p className="home__quiet">
                    Save this signing secret in the receiver now. It is the key the signature is
                    made with, and this is the only time it is shown — if it is lost, remove the
                    endpoint and add it again.
                  </p>
                  <p className="secret mono" data-webhook-secret>
                    {hookSecret}
                  </p>
                  <div className="settings__row">
                    <button className="btn" onClick={() => setHookSecret(null)}>
                      I have saved it
                    </button>
                  </div>
                </>
              )}

              <label className="field">
                <span className="label">Where to send them</span>
                <div className="settings__row">
                  <input
                    type="url"
                    inputMode="url"
                    placeholder="https://example.com/hooks/staleless"
                    aria-label="Address to send events to"
                    value={hookUrl}
                    disabled={!mayChange}
                    onChange={(e) => setHookUrl(e.target.value)}
                  />
                  {/* A repeated add — a workspace can have several endpoints — which is
                  exactly what .btn--tinted was written for. */}
                  <button
                    className="btn btn--tinted"
                    disabled={!mayChange || hookUrl.trim().length === 0 || hookBusy !== null}
                    onClick={() =>
                      void hookAct(
                        'add',
                        async () => {
                          const { secret } = await api.addWebhook(hookUrl.trim())
                          setHookUrl('')
                          setHookSecret(secret)
                        },
                        'Added. Copy the signing secret — it is shown only once.',
                      )
                    }
                  >
                    {hookBusy === 'add' ? (
                      <Icon name="spinner" size={13} />
                    ) : (
                      <Icon name="plus" size={13} />
                    )}
                    Add endpoint
                  </button>
                </div>
              </label>

              {hooks.endpoints.length === 0 ? (
                /* No button here on purpose: the one thing to do is the field
               directly above, and a second control that only moved the cursor
               into it would be an action invented to fill a space. */
                <p className="nothing">
                  Nothing is told when this site publishes. Paste the address of a receiver in the
                  field above and add it — you will be shown a signing secret once.
                </p>
              ) : (
                <ul className="people">
                  {hooks.endpoints.map((endpoint) => (
                    <li key={endpoint.id} className="people__row people__row--wrap">
                      <span className="people__who">
                        <span className="people__name">
                          {endpoint.url}
                          {endpoint.enabled ? (
                            <span className="badge badge--accent">on</span>
                          ) : (
                            <span className="badge badge--danger">off</span>
                          )}
                        </span>
                        <span className="people__email">
                          {endpoint.enabled
                            ? endpoint.lastSuccessAt
                              ? `Last delivered ${timeAgo(endpoint.lastSuccessAt)}`
                              : `Added ${timeAgo(endpoint.createdAt)} by ${endpoint.createdName}. Nothing delivered yet.`
                            : (endpoint.disabledReason ??
                              'Turned off. It is told nothing while it is off.')}
                        </span>
                      </span>
                      <button
                        className="btn btn--ghost btn--sm"
                        disabled={!mayChange || hookBusy !== null}
                        onClick={() =>
                          void hookAct(
                            `test:${endpoint.id}`,
                            () => api.testWebhook(endpoint.id),
                            'Test event queued. It appears in the log below within a few seconds.',
                          )
                        }
                        data-tip="Send this endpoint a test event now, without publishing anything"
                      >
                        {hookBusy === `test:${endpoint.id}` ? (
                          <Icon name="spinner" size={13} />
                        ) : null}
                        Send a test event
                      </button>
                      <button
                        className="btn btn--ghost btn--sm"
                        disabled={!mayChange || hookBusy !== null}
                        onClick={() =>
                          void hookAct(
                            `toggle:${endpoint.id}`,
                            () => api.setWebhookEnabled(endpoint.id, !endpoint.enabled),
                            endpoint.enabled
                              ? 'Turned off. It will be told nothing until you turn it back on.'
                              : 'Turned back on. The next publish will reach it.',
                          )
                        }
                      >
                        {hookBusy === `toggle:${endpoint.id}` ? (
                          <Icon name="spinner" size={13} />
                        ) : null}
                        {endpoint.enabled ? 'Turn off' : 'Turn back on'}
                      </button>
                      {/* The question the box used to ask, in the row that holds
                      the address it is about. A `window.confirm` printed that
                      address into a sentence the operating system drew OVER
                      the list, so the one thing worth checking — is this the
                      right endpoint — was behind the question about it. */}
                      {removingHook === endpoint.id ? null : (
                        <button
                          className="btn btn--danger btn--sm"
                          disabled={!mayChange || hookBusy !== null}
                          onClick={() => setRemovingHook(endpoint.id)}
                        >
                          Remove
                        </button>
                      )}
                      {removingHook === endpoint.id && (
                        <div className="pubs__confirm">
                          <p className="field__note">
                            This endpoint is told nothing more. The delivery log keeps everything
                            already sent to it. Adding the same address again gives you a new
                            signing secret, so whatever is checking signatures at the other end has
                            to be told the new one.
                          </p>
                          <div className="pubs__actions">
                            <button
                              className="btn btn--danger btn--sm"
                              disabled={hookBusy !== null}
                              onClick={() => {
                                setRemovingHook(null)
                                void hookAct(
                                  `remove:${endpoint.id}`,
                                  () => api.removeWebhook(endpoint.id),
                                  'Removed. The delivery log keeps what was already sent to it.',
                                )
                              }}
                            >
                              {hookBusy === `remove:${endpoint.id}` ? (
                                <Icon name="spinner" size={13} />
                              ) : null}
                              Remove it
                            </button>
                            <button
                              className="btn btn--ghost btn--sm"
                              disabled={hookBusy !== null}
                              onClick={() => setRemovingHook(null)}
                            >
                              Keep it
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              <p className="home__quiet">
                An endpoint that cannot be reached is tried {hooks.maxAttempts} times, waiting
                longer each time. After {hooks.disableAfter} events in a row have failed that way it
                is turned off automatically and the workspace’s owners are told — retrying a
                receiver that has gone away for ever is how a sender ends up blocked. Turning it
                back on is one press.
              </p>
            </Card>
          )}

          {mayReadWebhooks && hooks && (
            <Card at="org/webhooks" here={where} said={said} owns={['hook:']} data-webhook-log>
              <h2>Webhook deliveries</h2>
              <p className="home__quiet">
                What was sent, when, what came back, and how many attempts it took. Kept for 30
                days.
              </p>
              {hooks.deliveries.length === 0 ? (
                hooks.endpoints.length === 0 ? (
                  /* Two empty states behind one card, and they must not be the
                 same sentence: nothing has been sent because nothing is
                 listening is a different problem from nothing has been sent
                 yet. No action on this one — the thing to do is in the card
                 above, and repeating it here would be two doors to one room. */
                  <p className="nothing">
                    Nothing has been sent, because nothing is listening yet. Add an endpoint in the
                    card above and the first delivery will appear here.
                  </p>
                ) : (
                  <>
                    <p className="nothing">
                      Nothing has been sent yet. Publishing this site sends the first event — or
                      send a test event now to check the receiver works before you rely on it.
                    </p>
                    <div className="settings__row">
                      <button
                        className="btn"
                        disabled={!mayChange || hookBusy !== null}
                        onClick={() =>
                          void hookAct(
                            `test:${hooks.endpoints[0]!.id}`,
                            () => api.testWebhook(hooks.endpoints[0]!.id),
                            'Test event queued. It appears here within a few seconds.',
                          )
                        }
                      >
                        {hookBusy === `test:${hooks.endpoints[0]!.id}` ? (
                          <Icon name="spinner" size={13} />
                        ) : null}
                        Send a test event
                      </button>
                    </div>
                  </>
                )
              ) : (
                <ul className="people">
                  {hooks.deliveries.map((row) => (
                    <li key={row.id} className="people__row people__row--wrap">
                      <span className="people__who">
                        <span className="people__name">
                          {row.event}
                          <span className={deliveryBadge(row)}>{describeDelivery(row)}</span>
                        </span>
                        <span className="people__email">
                          {timeAgo(row.queuedAt)} · {row.url} ·{' '}
                          {row.responseStatus === null
                            ? 'no answer'
                            : `answered ${row.responseStatus}`}
                          {row.durationMs === null ? '' : ` in ${row.durationMs}ms`} ·{' '}
                          {`${row.attempts} of ${hooks.maxAttempts} attempts`}
                        </span>
                        {row.error && (
                          /* The sentence, not the stack. `errorDetail` is kept
                         beside it in the database for whoever has to debug
                         it, and is deliberately not on this screen: it is a
                         receiver's own error page, and pasting one into a
                         settings screen is how somebody else's HTML ends up
                         being read as ours. */
                          <span className="people__email">{row.error}</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}

          {mayChange && (
            <Card at="org/activity" here={where}>
              <h2>Activity log</h2>
              <p className="home__quiet">
                Sensitive changes to this workspace — publishing, membership, domains and
                passphrases — with who did them and when. An activity log names people's actions, so
                it is shown here to an owner or admin only, the same as the rest of this screen.
              </p>
              {auditError && (
                <p className="screen__error">
                  <Icon name="alert" size={13} /> {auditError}
                </p>
              )}
              {/* No action here, deliberately: a log is written BY doing the
              things it records, so the only honest way to fill it is to use
              the product. Nothing to offer, and nothing hidden either. */}
              {auditRows && auditRows.length === 0 && (
                <p className="nothing">Nothing recorded yet.</p>
              )}
              {auditRows && auditRows.length > 0 && (
                <ul className="people">
                  {auditRows.map((row) => {
                    const detail = auditDetail(row)
                    return (
                      <li key={row.id} className="people__row">
                        <span className="people__who">
                          <span className="people__name">
                            {row.actorName ?? (row.actorKind === 'mcp' ? 'An agent' : 'Someone')}{' '}
                            {describeAudit(row.action)}
                          </span>
                          <span className="people__email">
                            {timeAgo(row.createdAt)}
                            {detail ? ` · ${detail}` : ''}
                          </span>
                        </span>
                      </li>
                    )
                  })}
                </ul>
              )}
              {auditNext && (
                <div className="settings__row">
                  <button
                    className="btn btn--ghost btn--sm"
                    disabled={auditBusy}
                    onClick={() => void loadMoreAudit()}
                  >
                    {auditBusy ? <Icon name="spinner" size={13} /> : null}
                    Load more
                  </button>
                </div>
              )}
            </Card>
          )}

          {/*
        Plan, and the way out of it (§56).

        A paying customer could upgrade in two clicks and had to write to
        support to stop — the asymmetry §56 names as a dark pattern. Stripe
        hosts the card, the invoices and the cancellation, so this card is a
        button and the four things §56 asks a cancellation to state: when it
        takes effect, what stops working, what happens to the content, and
        whether the export is still there.

        Said BEFORE the button rather than on the page after it: somebody
        deciding whether to cancel is deciding here, and finding out what it
        costs them on Stripe's screen is finding out too late.

        Owner or billing access, like the upgrade beside the CSS field: money
        is not implied by admin, and the route reads the same explicit grant.
      */}
          <Card at="org/plan" here={where} said={said} owns={['billing', 'buy-']} data-plan-card>
            <h2>Plan</h2>
            <dl className="settings__facts">
              <dt>This workspace</dt>
              <dd data-plan-name>
                {settings.plan.label}
                {settings.plan.plan === 'free' && (
                  <span className="field__note">
                    Nothing is being charged. Pro is offered beside the features it includes — your
                    own CSS, under Theme above.
                  </span>
                )}
              </dd>
            </dl>

            {/*
            The four tiers, and what each one is FOR.

            Rendered from what the server sent, not from a list written here:
            `plans.ts` decides what a plan includes and what it costs, and a
            second copy of the prices in this file is a copy that goes stale on
            the day one of them changes.

            Four, and Agency is not one of them. An agency with thirty clients
            needs client isolation, cloning, client branding and
            transfer-to-client — §57, deferred — and none of that is a bigger
            allowance, so a card selling it at $149 would be selling a workflow
            nobody built.

            Enterprise has no checkout button. It is a buying model rather than
            a larger self-serve tier: nobody moves up for one more request a
            day, they move because procurement wants directory sign-in, a DPA,
            an SLA and a security review. So the card says what is built and
            what still needs agreement.
          */}
            {(settings.plans ?? []).length > 0 && (
              <div className="plans" data-plans>
                <div className="plans__billing-toggle" data-billing-interval>
                  <button
                    className={`btn btn--sm${billingInterval === 'month' ? ' btn--tinted' : ''}`}
                    aria-pressed={billingInterval === 'month'}
                    onClick={() => setBillingInterval('month')}
                  >
                    Monthly
                  </button>
                  <button
                    className={`btn btn--sm${billingInterval === 'year' ? ' btn--tinted' : ''}`}
                    aria-pressed={billingInterval === 'year'}
                    onClick={() => setBillingInterval('year')}
                  >
                    Yearly · 2 months free
                  </button>
                </div>
                {(settings.plans ?? []).map((tier) => {
                  const here = tier.name === settings.plan.plan
                  const shipping = tier.adds.filter((claim) => claim.standing !== 'on_request')
                  const asked = tier.adds.filter((claim) => claim.standing === 'on_request')
                  return (
                    <article
                      key={tier.name}
                      className={`plan${here ? ' plan--here' : ''}`}
                      data-plan-tier={tier.name}
                      {...(here ? { 'data-plan-current': '' } : {})}
                    >
                      <div className="plan__top">
                        <h3 className="plan__name">{tier.label}</h3>
                        {here && <span className="plan__badge">Current</span>}
                      </div>
                      <p className="plan__price" data-plan-price>
                        {tier.monthlyCents === null ? (
                          'Let’s talk'
                        ) : tier.monthlyCents === 0 ? (
                          'Free'
                        ) : (
                          <>
                            ${Math.round(tier.monthlyCents / 100)}
                            <span className="plan__per"> / month</span>
                          </>
                        )}
                      </p>
                      <p className="plan__audience">{tier.audience}</p>
                      {/* The reason to move up, which is never the counts. */}
                      <p className="plan__promise">{tier.promise}</p>
                      <p className="plan__includes">Included</p>
                      <ul className="plan__list">
                        {shipping.map((claim) => (
                          <li key={claim.says}>{claim.says}</li>
                        ))}
                      </ul>
                      {asked.length > 0 && (
                        <>
                          {/*
                          Said plainly, because the alternative is a tick beside
                          something that does not exist. These are what the
                          conversation is about; they are not switched on by
                          paying, and nobody should discover that afterwards.
                        */}
                          <p className="plan__on-request" data-plan-on-request>
                            On request, and part of what we agree — not switched on by paying:
                          </p>
                          <ul className="plan__list plan__list--muted">
                            {asked.map((claim) => (
                              <li key={claim.says}>{claim.says}</li>
                            ))}
                          </ul>
                        </>
                      )}
                      <div className="plan__act">
                        {here ? (
                          <span className="field__note">This workspace is on this plan.</span>
                        ) : tier.buy === 'contact' ? (
                          <a className="btn btn--sm" href={ENTERPRISE_MAILTO} data-plan-contact>
                            <Icon name="link" size={13} />
                            Talk to us
                          </a>
                        ) : tier.buy === 'checkout' &&
                          mayManageBilling &&
                          settings.plan.plan === 'free' &&
                          settings.emailConfirmed !== false ? (
                          <button
                            className="btn btn--tinted btn--sm"
                            data-plan-buy={tier.name}
                            disabled={busy !== null}
                            onClick={() =>
                              void act(
                                `buy-${tier.name}`,
                                async () => {
                                  const { url } = await api.startUpgrade(
                                    tier.name as 'pro' | 'business',
                                    billingInterval,
                                  )
                                  // Stripe's own page, in this tab: a payment form
                                  // in a new window is one a pop-up blocker eats.
                                  window.location.assign(url)
                                },
                                'Taking you to Stripe…',
                              )
                            }
                          >
                            {busy === `buy-${tier.name}` ? (
                              <Icon name="spinner" size={13} />
                            ) : (
                              <Icon name="plus" size={13} />
                            )}
                            Choose {tier.label}
                          </button>
                        ) : null}
                      </div>
                    </article>
                  )
                })}
              </div>
            )}
            {/*
            A payment that did not go through, while it can still be fixed
            (§56). Stripe's dunning retries for about two weeks and the plan is
            untouched the whole time — so the grace period exists and was
            invisible: the first anybody here heard of a failed card was the
            cancellation, when the plan dropped to Free.

            What it must say is that nothing is switched off, because that is
            the fear, and where the card is, because that is the fix.
          */}
            {settings.billingStatus === 'past_due' && (
              <p className="field__note" data-payment-failed>
                A payment did not go through. Stripe is trying again over the next two weeks, and
                nothing is switched off in the meantime — every page, draft and image is where it
                was. The card is on the billing page below.
              </p>
            )}
            {settings.plan.plan !== 'free' && (
              <>
                <p className="home__quiet" data-cancel-terms>
                  Cancelling takes effect at the end of the period you have paid for — nothing stops
                  today. After that this workspace is on Free: your own CSS stops being applied, a
                  custom domain stops being served, and the daily model allowance returns to Free’s.
                  Your pages, drafts, images and history are not touched, and Export — in this
                  organisation’s settings — works on every plan, before or after.
                </p>
                {mayManageBilling && (
                  <div className="settings__row">
                    <button
                      className="btn btn--sm"
                      data-manage-billing
                      disabled={busy !== null}
                      onClick={() =>
                        void act(
                          'billing',
                          async () => {
                            const { url } = await api.openBilling()
                            // Stripe's own page, in this tab, for the same reason
                            // the checkout is: a pop-up blocker eats the other way.
                            window.location.assign(url)
                          },
                          'Taking you to Stripe…',
                        )
                      }
                    >
                      {busy === 'billing' ? (
                        <Icon name="spinner" size={13} />
                      ) : (
                        <Icon name="link" size={13} />
                      )}
                      Manage billing or cancel
                    </button>
                    <span className="field__note">
                      Stripe holds the card and the invoices; we never see the number.
                    </span>
                  </div>
                )}
              </>
            )}
          </Card>

          <Card at="org/export" here={where} said={said} owns={['export']}>
            <h2>Take your content with you</h2>
            <p className="home__quiet">
              One zip: every page as plain Markdown with its titles, descriptions and redirects, the
              navigation, every image, every frozen version under its own name, and a manifest
              naming what is inside. Nothing here is locked in — this is the same content the site
              is built from.
            </p>
            <div className="settings__row">
              <button className="btn" disabled={busy !== null} onClick={() => void download()}>
                {busy === 'export' ? <Icon name="spinner" size={13} /> : null}
                Download everything
              </button>
            </div>
          </Card>

          {/*
        Changing a password was not possible from inside the product: the only
        path to a new one was the emailed reset link, so a deployment with no
        mail provider had none at all. The card says what happens to the other
        sessions BEFORE the button is pressed, because that is the part
        somebody would otherwise discover on their phone.
      */}
          {/*
        The name on every revision, and it could not be corrected by the person
        it belongs to — a typo on the first day followed them through the whole
        history. The address is shown and not editable: it is the sign-in
        identity, and moving it needs the new one verified.
      */}
          {/*
            What the workspace is called, on the section whose sentence
            promises "the workspace itself". Outside the projects condition
            below: a workspace has a name whether or not it has more than one
            documentation site in it.
          */}
          <Card at="org/general" here={where} data-workspace-card>
            <WorkspaceIdentity mayChange={mayDecideAsk} />
          </Card>

          {projects.length > 0 && mayChange && (
            <Card at="org/general" here={where} data-projects-card>
              <h2>Projects</h2>
              <p className="home__quiet">
                Each one is its own documentation site, with its own repository. Removing a project
                takes its drafts, previews and publication history; the repository — where the words
                are — is left exactly where it is.
              </p>
              <ul className="people">
                {projects.map((project) => (
                  <li key={project.id} className="people__row" data-project-row>
                    <span className="people__who">
                      <span className="people__name">
                        {project.name}
                        {project.slug === activeProject && <span className="badge">this one</span>}
                      </span>
                      <span className="people__email">
                        <code>{project.slug}</code>
                      </span>
                    </span>
                    <button
                      className="btn btn--ghost btn--sm"
                      disabled={busy !== null || projects.length === 1}
                      data-tip={
                        projects.length === 1
                          ? 'A workspace needs a project. Make another one first.'
                          : 'Remove this project'
                      }
                      onClick={() => {
                        if (removing !== project.id) {
                          setRemoving(project.id)
                          return
                        }
                        setRemoving(null)
                        void act(
                          `project-${project.id}`,
                          async () => {
                            await api.removeProject(project.id)
                            await loadProjects()
                          },
                          `${project.name} removed. Its repository is untouched.`,
                        )
                      }}
                    >
                      {busy === `project-${project.id}` ? <Icon name="spinner" size={12} /> : null}
                      {removing === project.id ? 'Really remove?' : 'Remove'}
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <Card
            at="account/profile"
            here={where}
            said={said}
            owns={['digest', 'email', 'notifications', 'profile']}
            data-you-card
          >
            <h2>You</h2>
            <p className="home__quiet">
              This name appears on every page you change, in the people list and in the audit trail.
            </p>
            <label className="field">
              <span className="label">Your name</span>
              <div className="settings__row">
                <input
                  className="input"
                  data-display-name
                  placeholder="Your name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
                <button
                  className="btn"
                  disabled={busy !== null || !displayName.trim()}
                  onClick={() =>
                    void act(
                      'profile',
                      () => api.updateProfile({ displayName: displayName.trim() }),
                      'Name changed. It shows on what you change from now on.',
                    )
                  }
                >
                  {busy === 'profile' ? <Icon name="spinner" size={13} /> : null}
                  Save name
                </button>
              </div>
            </label>
            {/*
          The address itself, under the name, because they are the same kind of
          fact about a person — and the only two an account carries.
        */}
            <p className="home__quiet">
              You sign in with <b>{signInEmail ?? 'this address'}</b>. Moving it needs the new
              address confirmed first: nothing changes until you open the link sent there.
            </p>
            <div className="settings__row">
              {/* A label each, because the row holds two different questions:
                  where to move the account, and proof it is you asking. */}
              <label className="field">
                <span className="label">New email address</span>
                <input
                  className="input"
                  data-new-email
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                />
              </label>
              <label className="field">
                <span className="label">Your current password</span>
                <input
                  className="input"
                  data-email-password
                  type="password"
                  autoComplete="current-password"
                  value={emailPassword}
                  onChange={(e) => setEmailPassword(e.target.value)}
                />
              </label>
              <button
                className="btn"
                data-send-email-change
                disabled={busy !== null || !newEmail.trim() || !emailPassword}
                onClick={() =>
                  void act(
                    'email',
                    async () => {
                      const { sentTo } = await api.changeEmail(newEmail.trim(), emailPassword)
                      setEmailSentTo(sentTo)
                      setEmailPassword('')
                      setNewEmail('')
                    },
                    'Confirmation sent. Nothing changes until you open it.',
                  )
                }
              >
                {busy === 'email' ? <Icon name="spinner" size={13} /> : null}
                Send a confirmation
              </button>
            </div>
            {emailSentTo && (
              <p className="home__quiet" data-email-pending>
                Waiting for <b>{emailSentTo}</b> to be confirmed. The link works once and expires in
                an hour; the address on the account is still the old one until then.
              </p>
            )}
            {/*
          The other half of the bell. A mention is addressed to a person, and
          the person it is addressed to is often not looking at the product —
          the reviewer invited yesterday most of all. On by default, and this
          is where it goes off, which is what every letter's last line says.
        */}
            <label className="settings__row">
              <input
                type="checkbox"
                data-email-notifications
                checked={emailNotifications}
                disabled={busy !== null}
                onChange={(e) => {
                  const wanted = e.target.checked
                  setEmailNotifications(wanted)
                  void act(
                    'notifications',
                    () => api.updateProfile({ emailNotifications: wanted }),
                    wanted
                      ? 'Mentions and review requests will reach you by email as well.'
                      : 'Only the bell from now on.',
                  )
                }}
              />
              <span>Email me about mentions, reviews and publish results</span>
            </label>
            {/*
          A second switch, because it is a different kind of letter. The one
          above is about something that happened TO you; this is a summary
          nobody asked for, on a schedule. Wanting the first and not the second
          is an ordinary thing to want, and before this the only way to say it
          was to turn off both.
        */}
            <label className="settings__row">
              <input
                type="checkbox"
                data-digest
                checked={digest === 'weekly'}
                disabled={busy !== null}
                onChange={(e) => {
                  const wanted = e.target.checked ? 'weekly' : 'off'
                  setDigest(wanted)
                  void act(
                    'digest',
                    () => api.updateProfile({ digest: wanted }),
                    wanted === 'weekly'
                      ? 'A summary of the week will arrive on Mondays.'
                      : 'No more weekly summaries.',
                  )
                }}
              />
              <span>Send me a weekly summary of what changed</span>
            </label>
          </Card>

          {/*
        A changelog the documentation writes about itself.

        Every publish is recorded with its message and the pages it touched, and
        a reader asks what changed far more often than a writer remembers to
        tell them. Asking somebody to keep a hand-written changelog beside the
        site is asking them to do twice what the product already knows once —
        and the hand-written copy is the one that goes stale.
      */}
          <Card at="site/general" here={where} said={said} owns={['changelog']} data-changelog-card>
            <h2>Changelog</h2>
            <p className="home__quiet">
              A page listing every publish, newest first, with the pages each one touched. Written
              onto a draft; anything you write above or below the generated part is kept when it is
              written again.
            </p>
            <div className="settings__row">
              <button
                className="btn btn--tinted btn--sm"
                data-write-changelog
                disabled={busy !== null}
                onClick={() =>
                  void act(
                    'changelog',
                    async () => {
                      const out = await api.writeChangelog()
                      setChangelog(out)
                    },
                    'The changelog is on a draft. Read it, then publish when it is right.',
                  )
                }
              >
                {busy === 'changelog' ? (
                  <Icon name="spinner" size={13} />
                ) : (
                  <Icon name="page" size={13} />
                )}
                Write a changelog
              </button>
              <span className="field__note">
                From your publish history — never straight onto the live site.
              </span>
            </div>
            {changelog && (
              <p className="home__quiet" data-changelog-said>
                {changelog.entries} publish{changelog.entries === 1 ? '' : 'es'} written to{' '}
                <code>{changelog.branch}</code>
                {changelog.kept && ', keeping what you had written around it'}.
              </p>
            )}
          </Card>

          <Card
            at="account/security"
            here={where}
            said={said}
            owns={['password']}
            data-password-card
          >
            <h2>Password</h2>
            <p className="home__quiet">
              Changing it signs you out everywhere else. You stay signed in here.
            </p>
            <div className="settings__row">
              {/* A label each: the row asks for the password you have and the
                  one you want, and one heading over both names neither. */}
              <label className="field">
                <span className="label">Current password</span>
                <input
                  className="input"
                  type="password"
                  autoComplete="current-password"
                  data-current-password
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </label>
              <label className="field">
                <span className="label">New password</span>
                <input
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  data-new-password
                  placeholder="At least 12 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </label>
              <button
                className="btn"
                disabled={busy !== null || !currentPassword || !newPassword}
                onClick={() =>
                  void act(
                    'password',
                    async () => {
                      await api.changePassword(currentPassword, newPassword)
                      setCurrentPassword('')
                      setNewPassword('')
                    },
                    'Password changed. Every other browser has been signed out.',
                  )
                }
              >
                {busy === 'password' ? <Icon name="spinner" size={13} /> : null}
                Change password
              </button>
            </div>
          </Card>

          {twoFactor && (
            <Card at="account/security" here={where} said={said} owns={['totp']} data-totp-card>
              <h2>
                Two-step sign-in
                {twoFactor.enabled && <span className="badge badge--accent">on</span>}
              </h2>

              {recoveryCodes ? (
                /* Shown once, and there is no route that can show them again — one
               that could would be a way around the second factor. */
                <>
                  <p className="home__quiet">
                    Two-step sign-in is on. Save these recovery codes somewhere that is not this
                    account: each one works once, and they are the only way back in if you lose the
                    authenticator. This is the only time they are shown.
                  </p>
                  <ul className="totp__codes mono" data-recovery-codes>
                    {recoveryCodes.map((one) => (
                      <li key={one}>{one}</li>
                    ))}
                  </ul>
                  <div className="settings__row">
                    <button
                      className="btn btn--primary"
                      onClick={() => downloadRecoveryCodes(recoveryCodes)}
                    >
                      Download recovery codes
                    </button>
                    <button className="btn" onClick={() => setRecoveryCodes(null)}>
                      I have saved them
                    </button>
                  </div>
                </>
              ) : enrolling ? (
                <>
                  <p className="home__quiet">
                    Add this account to your authenticator, then type the six digits it shows.
                    Nothing changes until you do — leaving this page now leaves the account exactly
                    as it is.
                  </p>
                  <TotpQr uri={enrolling.uri} />
                  <p className="home__quiet">
                    Scan the code with your authenticator. On this device,{' '}
                    <a href={enrolling.uri}>open it directly</a>. Or type the key in by hand:
                  </p>
                  <p className="secret mono" data-totp-secret>
                    {enrolling.readable}
                  </p>
                  <label className="field">
                    <span className="label">The six-digit code from your authenticator</span>
                    <div className="settings__row">
                      <input
                        className="input"
                        autoComplete="one-time-code"
                        placeholder="Six-digit code"
                        value={totpCode}
                        disabled={totpBusy}
                        onChange={(e) => setTotpCode(e.target.value)}
                      />
                      <button
                        className="btn btn--primary"
                        disabled={totpBusy}
                        onClick={() => void finishTwoFactor()}
                      >
                        {totpBusy ? <Icon name="spinner" size={13} /> : null}
                        Turn it on
                      </button>
                      <button
                        className="btn btn--ghost"
                        disabled={totpBusy}
                        onClick={() => {
                          setEnrolling(null)
                          setTotpCode('')
                          setTotpError(null)
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </label>
                </>
              ) : twoFactor.enabled ? (
                <>
                  <p className="home__quiet">
                    Signing in asks for your password and then a code from your authenticator, so a
                    stolen password is not enough on its own. You have {twoFactor.recoveryCodesLeft}{' '}
                    recovery {twoFactor.recoveryCodesLeft === 1 ? 'code' : 'codes'} left.
                  </p>
                  <p className="home__quiet">
                    Turning it off needs a code as well — a recovery code counts, which is the
                    answer for somebody whose authenticator is gone.
                  </p>
                  <label className="field">
                    <span className="label">A code, to prove it is you</span>
                    <div className="settings__row">
                      <input
                        className="input"
                        autoComplete="one-time-code"
                        placeholder="Code, or a recovery code"
                        value={totpCode}
                        disabled={totpBusy}
                        onChange={(e) => setTotpCode(e.target.value)}
                      />
                      <button
                        className="btn btn--danger"
                        disabled={totpBusy}
                        onClick={() => setStoppingTwoFactor(true)}
                      >
                        {totpBusy ? <Icon name="spinner" size={13} /> : null}
                        Turn it off
                      </button>
                    </div>
                  </label>
                  {stoppingTwoFactor && (
                    <Confirm
                      says="Your password becomes the only thing in front of this account. Anybody who learns it is signed in — no code, no device, nothing else to get past."
                      does="Turn it off"
                      cancel="Keep it on"
                      busy={totpBusy}
                      onYes={() => {
                        setStoppingTwoFactor(false)
                        void stopTwoFactor()
                      }}
                      onNo={() => setStoppingTwoFactor(false)}
                    />
                  )}
                </>
              ) : (
                <>
                  <p className="home__quiet">
                    Ask for a code from an authenticator app as well as your password. A password
                    that leaks — reused, phished, or in somebody else’s breach — then gets nobody
                    in.
                    {twoFactor.pending
                      ? ' You started setting this up and never finished, so nothing is on yet.'
                      : ''}
                  </p>
                  <div className="settings__row">
                    <button
                      className="btn"
                      disabled={totpBusy}
                      onClick={() => void beginTwoFactor()}
                    >
                      {totpBusy ? (
                        <Icon name="spinner" size={13} />
                      ) : (
                        <Icon name="lock" size={13} />
                      )}
                      {twoFactor.pending ? 'Start again' : 'Set it up'}
                    </button>
                  </div>
                </>
              )}

              {totpError && (
                <p className="screen__error" role="alert">
                  <Icon name="alert" size={13} /> {totpError}
                </p>
              )}
            </Card>
          )}

          <Card at="account/security" here={where} said={said} owns={['sessions']}>
            <h2>Signed-in devices</h2>
            <p className="home__quiet">
              Every browser currently signed in as you. Not seeing yourself here is not something
              this screen can help with — sign in again.
            </p>
            {sessionsError && (
              <p className="screen__error">
                <Icon name="alert" size={13} /> {sessionsError}
              </p>
            )}
            {sessions && (
              <ul className="people">
                {sessions.map((s) => (
                  <li
                    key={s.id}
                    className={`people__row${signOutHere === s.id ? ' people__row--wrap' : ''}`}
                  >
                    <span className="people__who">
                      <span className="people__name">
                        {describeDevice(s.userAgent)}
                        {s.current && <span className="badge badge--accent">this device</span>}
                      </span>
                      <span className="people__email">
                        Active {timeAgo(s.lastActiveAt)}
                        {/* Only when something in front of us said so. A session
                        with no region says nothing rather than guessing at a
                        place, which is the one answer worth avoiding on a
                        screen whose job is "was this you". */}
                        {s.region && ` · ${regionName(s.region)}`}
                      </span>
                    </span>
                    {/* Revoking somebody ELSE'S device needs no question: it is the
                    thing this list exists for, and it costs that browser a
                    sign-in. Revoking this one ends the session doing the
                    revoking, which is a different act and is asked for. */}
                    {signOutHere === s.id ? null : (
                      <button
                        className="btn btn--danger btn--sm"
                        disabled={busy !== null}
                        onClick={() => (s.current ? setSignOutHere(s.id) : void revoke(s))}
                      >
                        {busy === `session:${s.id}` ? <Icon name="spinner" size={13} /> : null}
                        {s.current ? 'Sign out' : 'Revoke'}
                      </button>
                    )}
                    {signOutHere === s.id && (
                      <Confirm
                        says="This is the browser you are using right now. Signing out here ends this session and returns you to the sign-in screen. Every other device in this list stays signed in."
                        does="Sign out here"
                        cancel="Stay signed in"
                        busy={busy !== null}
                        onYes={() => void revoke(s)}
                        onNo={() => setSignOutHere(null)}
                      />
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {(settings.you?.workspaceRole ?? settings.you?.role) === 'owner' && (
            <Card at="org/general" here={where} said={said} owns={['workspace-delete']} danger>
              <h2>Delete this workspace</h2>
              <p className="home__quiet">
                Everything stops at once: every docs site, every draft, every member’s access, and
                the domains stop answering immediately. It stays restorable for 30 days — ask
                support — and after that it is gone. Take your content with you first — Export, in
                this organisation's settings — and the download is complete.
              </p>
              <label className="field">
                <span className="label">Type the workspace name to confirm</span>
                <div className="settings__row">
                  <input
                    className="input"
                    placeholder={`Type the workspace name — ${settings.name}`}
                    value={deleteName}
                    disabled={deleting}
                    onChange={(e) => setDeleteName(e.target.value)}
                  />
                  <button
                    className="btn btn--danger"
                    disabled={deleting || deleteName.trim() !== settings.name}
                    onClick={() => void destroy()}
                    data-tip={
                      deleteName.trim() === settings.name
                        ? undefined
                        : 'Type the name exactly — deleting the wrong workspace is the mistake this prevents'
                    }
                  >
                    {deleting ? <Icon name="spinner" size={13} /> : <Icon name="trash" size={13} />}
                    Delete the workspace
                  </button>
                </div>
              </label>
            </Card>
          )}

          {versions !== null && (
            <Card at="site/versions" here={where} said={said} owns={['versions']}>
              <h2>Versions</h2>
              <p className="home__quiet">
                Freeze what is published now, so readers of the old release still find the
                documentation that matches it. A version is a branch in this site’s own repository —
                it stays readable at <code>/v/&lt;name&gt;/</code> and it exports with everything
                else. A version can be corrected: “Fix this version” starts a draft on it, and
                publishing that draft lands in the version rather than on the live documentation.
              </p>
              {versions.length === 0 ? (
                <p className="nothing">No versions yet — everything is “latest”.</p>
              ) : (
                <ul className="people">
                  {versions.map((v) => (
                    <li
                      key={v.name}
                      className={`people__row${retiring === v.name ? ' people__row--wrap' : ''}`}
                    >
                      <span className="people__who">
                        <span className="people__name">
                          {v.label}
                          {v.deprecated && (
                            <span className="badge badge--quiet">no longer current</span>
                          )}
                        </span>
                        <span className="people__email">
                          /v/{v.name}/ — frozen at the day it was cut
                        </span>
                      </span>
                      {mayChange && (
                        <>
                          {/*
                        The door out of a dead end. A version could be cut and
                        never corrected: a typo frozen into v1 stayed there,
                        because every draft published over the live site
                        whatever it was started from.
                      */}
                          <button
                            className="btn btn--ghost btn--sm"
                            disabled={versionBusy}
                            data-tip={`Start a draft on ${v.label}. Publishing it lands in this version, not on the live site.`}
                            onClick={() => {
                              setVersionBusy(true)
                              void api
                                .createBranch(`fix-${v.name}-${Date.now().toString(36)}`, v.name)
                                .then((draft) => {
                                  onEditVersion?.(draft.name)
                                })
                                .catch((e: unknown) =>
                                  setSaid({
                                    name: 'versions',
                                    ok: false,
                                    text: e instanceof Error ? e.message : String(e),
                                  }),
                                )
                                .finally(() => setVersionBusy(false))
                            }}
                          >
                            Fix this version
                          </button>
                          <button
                            className="btn btn--ghost btn--sm"
                            disabled={versionBusy}
                            onClick={() => {
                              setVersionBusy(true)
                              void api
                                .setVersionDeprecated(v.name, !v.deprecated)
                                .then(loadVersions)
                                .catch((e: unknown) =>
                                  setSaid({
                                    name: 'versions',
                                    ok: false,
                                    text: e instanceof Error ? e.message : String(e),
                                  }),
                                )
                                .finally(() => setVersionBusy(false))
                            }}
                            data-tip={
                              v.deprecated
                                ? 'Stop showing the old-version banner'
                                : 'Show readers a banner saying this version is no longer maintained'
                            }
                          >
                            {v.deprecated ? 'Still current' : 'Mark old'}
                          </button>
                          {retiring === v.name ? null : (
                            <button
                              className="btn btn--danger btn--sm"
                              disabled={versionBusy}
                              onClick={() => setRetiring(v.name)}
                            >
                              Retire
                            </button>
                          )}
                        </>
                      )}
                      {/* Asked in the row, where the address it is about is on
                      screen. The box that asked it before was drawn over the
                      list of versions — including the one being retired. */}
                      {retiring === v.name && (
                        <div className="pubs__confirm">
                          <p className="field__note">
                            Readers lose <code>/v/{v.name}/</code>: every link to a page in this
                            version stops working, and the version switcher stops offering it. The
                            branch stays in your repository — this is your history, not ours to
                            delete — so it can be published again.
                          </p>
                          <div className="pubs__actions">
                            <button
                              className="btn btn--danger btn--sm"
                              disabled={versionBusy}
                              onClick={() => {
                                setRetiring(null)
                                setVersionBusy(true)
                                void api
                                  .retireVersion(v.name)
                                  .then(loadVersions)
                                  .catch((e: unknown) =>
                                    setSaid({
                                      name: 'versions',
                                      ok: false,
                                      text: e instanceof Error ? e.message : String(e),
                                    }),
                                  )
                                  .finally(() => setVersionBusy(false))
                              }}
                            >
                              {versionBusy ? <Icon name="spinner" size={13} /> : null}
                              Retire it
                            </button>
                            <button
                              className="btn btn--ghost btn--sm"
                              disabled={versionBusy}
                              onClick={() => setRetiring(null)}
                            >
                              Keep publishing it
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {mayChange && (
                <div className="settings__row">
                  <button className="btn" disabled={versionBusy} onClick={() => setCutting(true)}>
                    {versionBusy ? (
                      <Icon name="spinner" size={13} />
                    ) : (
                      <Icon name="branch" size={13} />
                    )}
                    Freeze this as a version
                  </button>
                </div>
              )}
              {cutting && (
                <CreateDialog
                  title="Freeze this as a version"
                  icon="branch"
                  what="It takes a copy of what is published right now. Readers of the old release keep finding the documentation that matches it, and the live documentation carries on without them."
                  submitLabel="Freeze it"
                  fields={[
                    {
                      key: 'name',
                      label: 'Name in the address',
                      placeholder: 'v1',
                      mono: true,
                      note: 'Readers reach it at /v/<name>/, so this is part of every address in the frozen version. It cannot be changed afterwards.',
                    },
                    {
                      key: 'label',
                      label: 'What readers call it',
                      placeholder: 'Version 1',
                      note: 'The words in the version switcher and in the banner on every frozen page. Left empty it takes the name above.',
                    },
                  ]}
                  initial={{ name: '', label: '' }}
                  onClose={() => setCutting(false)}
                  onSubmit={(values) =>
                    cutVersion((values['name'] ?? '').trim(), values['label'] ?? '')
                  }
                />
              )}
            </Card>
          )}

          <Card at="account/profile" here={where} said={said} owns={['account-delete']} danger>
            <h2>Delete my account</h2>
            <p className="home__quiet">
              Deletes the PERSON, never the team’s work: pages, drafts and history stay, with your
              name on what you wrote. If you are the only owner of a workspace, transfer it or
              delete it first — the refusal will name which. You will be asked to confirm your
              identity.
            </p>
            <label className="field">
              <span className="label">Type your account email to confirm</span>
              <div className="settings__row">
                <input
                  className="input"
                  placeholder="Type your account email"
                  value={accountEmail}
                  disabled={accountBusy}
                  onChange={(e) => setAccountEmail(e.target.value)}
                />
                <button
                  className="btn btn--danger"
                  disabled={accountBusy || accountEmail.trim().length < 3}
                  onClick={() => void destroyAccount()}
                >
                  {accountBusy ? (
                    <Icon name="spinner" size={13} />
                  ) : (
                    <Icon name="trash" size={13} />
                  )}
                  Delete my account
                </button>
              </div>
            </label>
          </Card>
        </div>
      </div>
    </div>
  )
}
